import { collection, doc, getDoc, setDoc, updateDoc, query, where, getDocs, addDoc, deleteDoc, serverTimestamp, orderBy, limit, Timestamp, onSnapshot, writeBatch, increment, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { getIdToken } from '../contexts/utils/auth';
import { anonymousIdentityService } from './anonymousIdentityService';
import { getCurrentUserAnonymousId, isValidAnonymousId } from '../utils/anonymousIdUtils';
import { logError, logWarn, logDebug, log } from '../utils/logger';

// Initialize storage
export const storage = getStorage();

// Profile serving utilities
export interface ServedProfile {
  anonymousId: string; // Use anonymous ID instead of uid for public serving
  displayName: string;
  age: number;
  location: string;
  photos: PhotoData[];
  distance?: string;
  isOnline?: boolean;
  lastSeen?: Timestamp;
  
  // Mode-specific data
  mode: 'dating' | 'now';
  datingData?: {
    bio: string;
    prompts: DatingProfile['prompts'];
    relationshipGoals: string[];
    interests: string[];
    lifestyle: DatingProfile['lifestyle'];
  };
  nowData?: {
    quickStats: NowProfile['quickStats'];
    aboutMe: string;
    lookingFor: string[];
    availability: NowProfile['availability'];
    rightNow: boolean;
    tribes: string[];
  };
}

// Types (keeping your existing ones but fixing photos type)
export interface PhotoData {
  id: string;
  url: string;
  status: 'pending' | 'approved' | 'rejected' | 'blurred';
  isBlurred?: boolean;
  uploadedAt?: any;
  moderatedAt?: any;
  // New fields for mode-specific photo selection
  showInDating?: boolean;
  showInNow?: boolean;
}

// Dating-specific profile data
export interface DatingProfile {
  selectedPhotos: string[]; // Photo IDs to show in dating mode
  prompts: {
    question: string;
    answer: string;
    type?: 'text' | 'voice' | 'video';
  }[];
  bio: string;
  relationshipGoals: string[];
  lookingFor: string[];
  interests: string[];
  values: string[];
  dealBreakers: string[];
  lifestyle: {
    drinking?: string;
    smoking?: string;
    drugs?: string;
    pets?: string;
    kids?: string;
    exercise?: string;
  };
}

// Now Mode (hookup) specific profile data
export interface NowProfile {
  selectedPhotos: string[]; // Different photos for now mode
  quickStats: {
    build: string;
    height: string;
    ethnicity?: string;
    bodyHair?: string;
    tattoos?: string;
    piercings?: string;
  };
  lookingFor: string[]; // What they're looking for right now
  availability: 'available' | 'busy' | 'away' | 'offline';
  rightNow: boolean; // Available to meet right now
  aboutMe: string; // Short description for now mode
  tribes: string[]; // Community/scene affiliations
  kinks: string[];
  position?: string;
  safetyInfo: {
    lastTested?: Date;
    status?: string;
    prep?: boolean;
  };
}

// Privacy and visibility settings
export interface ModeVisibility {
  showInDating: boolean;
  showInNow: boolean;
  datingVisibleToAll: boolean;
  nowVisibleToAll: boolean;
  hideFromMatches: boolean; // Hide from people you've already matched with in other mode
}

// Comment system interfaces (Hinge-style specific element commenting)
export interface ProfileElementComment {
  id: string;
  fromAnonymousId: string; // Changed from fromUserId
  toAnonymousId: string; // Changed from toUserId
  elementType: 'photo' | 'prompt' | 'bio' | 'quickStats' | 'interests';
  elementId?: string; // For photos or prompts, the specific ID
  elementIndex?: number; // For photos or prompts, the array index
  comment: string;
  createdAt: Timestamp;
  isRead: boolean;
  replyTo?: string; // If replying to another comment
}

export interface ProfileInteraction {
  id: string;
  fromAnonymousId: string; // Changed from fromUserId
  toAnonymousId: string; // Changed from toUserId
  type: 'like' | 'comment' | 'favorite';
  mode: 'dating' | 'now';
  elementType?: 'photo' | 'prompt' | 'bio' | 'quickStats' | 'interests';
  elementId?: string;
  elementIndex?: number;
  comment?: string;
  createdAt: Timestamp;
  status: 'pending' | 'matched' | 'expired';
}

// Cruising spots interfaces (Sniffies-style location-based features)
export interface CruisingSpot {
  id: string;
  name: string;
  description: string;
  latitude: number;
  longitude: number;
  type: 'public' | 'private' | 'outdoor' | 'indoor' | 'bar' | 'club' | 'park' | 'beach' | 'gym';
  isActive: boolean;
  activeUsers: number;
  recentActivity: Timestamp;
  averageRating: number;
  totalRatings: number;
  createdBy: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  tags: string[];
  safetyRating: number;
  accessibilityInfo?: string;
  bestTimes: string[];
  rules?: string[];
  reports: number;
  isVerified: boolean;
  moderationStatus: 'approved' | 'pending' | 'flagged' | 'removed';
}

export interface UserLocationUpdate {
  userId: string;
  latitude: number;
  longitude: number;
  mode: 'dating' | 'now';
  isActive: boolean;
  availableUntil: Timestamp;
  lookingFor: string[];
  quickStats?: {
    build?: string;
    height?: string;
  };
  lastUpdated: Timestamp;
}

export interface SpotCheckIn {
  id: string;
  userId: string;
  spotId: string;
  checkedInAt: Timestamp;
  status: 'active' | 'left' | 'expired';
  lookingFor: string[];
  availableUntil: Timestamp;
  isVisible: boolean;
}

// Group Events/Meetups interfaces (Now Mode focused)
export interface GroupEvent {
  id: string;
  title: string;
  description: string;
  hostId: string;
  hostName: string;
  hostPhotoURL?: string;
  type: 'meetup' | 'party' | 'drinks' | 'dinner' | 'activity' | 'nightlife' | 'outdoors' | 'sports' | 'social';
  category: 'public' | 'private' | 'invite-only';
  maxAttendees?: number;
  currentAttendees: number;
  attendeeIds: string[];
  interestedIds: string[];
  location: {
    name: string;
    address: string;
    latitude: number;
    longitude: number;
    isOnline?: boolean;
  };
  dateTime: Timestamp;
  duration: number; // in minutes
  tags: string[];
  ageRange?: {
    min: number;
    max: number;
  };
  requirements?: string[];
  whatToBring?: string[];
  cost?: {
    amount: number;
    currency: string;
    description: string;
  };
  createdAt: Timestamp;
  updatedAt: Timestamp;
  status: 'upcoming' | 'active' | 'completed' | 'cancelled';
  isRecurring: boolean;
  recurringPattern?: 'weekly' | 'biweekly' | 'monthly';
  chatRoomId?: string;
  moderationStatus: 'approved' | 'pending' | 'flagged' | 'removed';
  reports: number;
}

export interface EventAttendee {
  id: string;
  eventId: string;
  userId: string;
  status: 'going' | 'interested' | 'declined' | 'no-show' | 'attended';
  joinedAt: Timestamp;
  rsvpedAt: Timestamp;
  checkInTime?: Timestamp;
  specialRequests?: string;
  emergencyContact?: {
    name: string;
    phone: string;
  };
}

export interface EventComment {
  id: string;
  eventId: string;
  userId: string;
  userName: string;
  userPhotoURL?: string;
  content: string;
  type: 'comment' | 'question' | 'update';
  createdAt: Timestamp;
  isFromHost: boolean;
  isPinned: boolean;
  replies?: EventComment[];
}
export interface UserProfile {
  uid: string; // Firebase Auth UID (internal only)
  anonymousId: string; // Public-facing anonymous identifier
  displayName: string;
  // email removed from profile for privacy (stored separately in identity mapping)
  photoURL: string;
  role: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  
  // Core shared profile details
  age: number;
  gender: string;
  customGender?: string;
  sexualOrientation?: string;
  pronouns?: string;
  customPronouns?: string;
  relationshipStatus?: string;
  location: string;
  neighborhood?: string;
  
  // All uploaded photos (master collection)
  photos: PhotoData[];
  
  // Mode-specific profile sections
  datingProfile: DatingProfile;
  nowProfile: NowProfile;
  
  // Privacy and visibility settings
  modeVisibility: ModeVisibility;
  
  // Activity and matching data
  isActive?: boolean;
  lastSeen?: Timestamp;
  onboardingComplete?: boolean;
  currentMode?: 'dating' | 'now'; // User's currently active mode
  
  stats?: {
    likes: number;
    matches: number;
    superLikes: number;
    views: number;
    messages?: number;
    // Mode-specific stats
    datingLikes?: number;
    nowTaps?: number;
  };
  
  // General preferences (shared between modes)
  preferences: {
    ageRange: [number, number];
    maxDistance: number;
    showMe: string;
    units: 'metric' | 'imperial';
  };
  
  // Legacy fields for backward compatibility
  bio?: string; // Will migrate to datingProfile.bio
  interests?: string[]; // Will migrate to datingProfile.interests
  intentions?: string[]; // Will migrate to datingProfile.relationshipGoals
  vibe?: string; // Will migrate appropriately

  // Beta program fields
  isBetaUser?: boolean;
  hasLifetimePremium?: boolean;
  betaJoinedAt?: Timestamp;
  selectedFeatures?: string[];
}
export interface UserSettings {
  uid: string;
  useMetric: boolean;
  genderPreferences: string[];
  relationshipStatusPreferences: string[];
  nowMode: boolean;
  // Privacy settings
  profileVisibility?: string; // 'public', 'matches', 'private'
  showOnlineStatus?: boolean;
  showLastActive?: boolean;
  showReadReceipts?: boolean;
  incognitoMode?: boolean;
  dataSharing?: boolean;
  locationPrecision?: string;
  
  // Profile Discovery Settings
  appModeVisibility?: string; // 'everyone', 'matches', 'hidden'
  showDistance?: boolean;
  allowMessagesFrom?: string; // 'everyone', 'matches', 'premium', 'none'
  
  // FMK Privacy Settings
  showMyChoices?: string; // 'everyone', 'matches', 'hidden'
  allowFMKFrom?: string; // 'everyone', 'verified', 'matches'
  
  // Cruising Privacy Settings
  cruisingVisible?: boolean;
  shareExactLocation?: boolean;
  cruisingHistory?: string; // 'public', 'matches', 'private'
  
  // Health & Activity Privacy Settings
  healthDataSharing?: string; // 'everyone', 'matches', 'private'
  activitySharing?: string; // 'public', 'matches', 'private'
  
  // Data Privacy Settings
  dataCollection?: boolean;
  analyticsSharing?: boolean;
  // Notification settings
  pushNotificationsEnabled: boolean;
  emailNotificationsEnabled: boolean;
  inAppNotificationsEnabled: boolean;
  // Other settings
  lastActive?: Timestamp;
  updatedAt?: Timestamp;
}
export interface AppSettings {
  id: string;
  // General Settings
  appName: string;
  maxProfilePhotos: number;
  maxBioLength: number;
  // Matching Settings
  maxDistance: number;
  ageRangeMin: number;
  ageRangeMax: number;
  // Moderation Settings
  autoModeratePhotos: boolean;
  autoModerateBios: boolean;
  reportThreshold: number;
  // Notification Settings
  enablePushNotifications: boolean;
  newMatchNotification: boolean;
  newMessageNotification: boolean;
  // System Settings
  maintenanceMode: boolean;
  debugMode: boolean;
  analyticsEnabled: boolean;
  // Email Settings
  resendApiKey: string;
  resendFromEmail: string;
  resendFromName: string;
  emailTemplateNewMatch: string;
  emailTemplateNewMessage: string;
  emailTemplateWeeklySummary: string;
  // Auth0 Settings
  auth0Domain: string;
  auth0ClientId: string;
  auth0RedirectUri: string;
  auth0Audience: string;
  auth0Scope: string;
  auth0Connection: string;
  auth0PromptLogin: boolean;
  // Feature settings
  features?: {
    [key: string]: {
      enabled: boolean;
      isPremium: boolean;
      limit?: number;
    };
  };
  // Timestamps
  updatedAt?: Timestamp;
}
export interface Conversation {
  id?: string;
  participants: string[];
  lastMessage: string;
  lastMessageTimestamp: Timestamp;
  unreadCount: {
    [uid: string]: number;
  };
  createdAt: Timestamp;
  updatedAt: Timestamp;
  // New fields for instant messaging
  type: 'matched' | 'direct' | 'comment_reply';
  mode: 'dating' | 'now';
  initiatedBy?: string; // Who started the conversation
  isAccepted?: boolean; // For direct messages, whether recipient has accepted
}
export interface Message {
  id?: string;
  conversationId: string;
  senderId: string;
  text: string;
  timestamp: Timestamp;
  read: boolean;
}
export interface Match {
  id?: string;
  users: string[];
  createdAt: Timestamp;
  matchedAt?: Timestamp;
}

// Additional interfaces for likes and matching
export interface LikeAction {
  id?: string;
  fromUserId: string;
  toUserId: string;
  action: 'like' | 'pass' | 'super_like';
  isSuper: boolean;
  createdAt: Timestamp;
}

// Prompt system interfaces
export interface Prompt {
  id?: string;
  text: string;
  category?: string;
  isActive: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  usageCount: number;
  likeCount: number;
  createdBy?: string; // Admin who created it
}

export interface PromptLike {
  id?: string;
  userId: string; // Person who liked the prompt
  targetUserId: string; // Owner of the prompt being liked
  promptId: string;
  createdAt: Timestamp;
  hasMatched?: boolean; // Whether these users have matched
}

// Collection references
const usersCollection = collection(db, 'users');
const userSettingsCollection = collection(db, 'userSettings');
const appSettingsCollection = collection(db, 'appSettings');
const conversationsCollection = collection(db, 'conversations');
const messagesCollection = collection(db, 'messages');
const matchesCollection = collection(db, 'matches');
const likesCollection = collection(db, 'likes');
const promptsCollection = collection(db, 'prompts');
const promptLikesCollection = collection(db, 'promptLikes');

// ==================== USER FUNCTIONS ====================

const getAuthToken = (): string | null => {
  return getIdToken();
};
const authenticatedGetDoc = async (docRef: any) => {
  const token = getAuthToken();
  if (!token) {
    logError('No auth token available for Firestore operation');
    throw new Error('Authentication required');
  }
  return getDoc(docRef);
};
export const getCurrentUser = async (): Promise<UserProfile | null> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      // Check for demo user
      const isDemoUser = localStorage.getItem('isDemoUser') === 'true';
      const demoUserType = localStorage.getItem('demoUserType');
      if (isDemoUser && demoUserType) {
        const demoUid = `demo-${demoUserType}`;
        return {
          uid: demoUid,
          displayName: `${demoUserType.charAt(0).toUpperCase() + demoUserType.slice(1)} User`,
          email: `${demoUserType}@thirstapp.com`,
          role: demoUserType,
          photoURL: ''
        } as UserProfile;
      }
      return null;
    }
    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        return userDoc.data() as UserProfile;
      } else {
        logDebug('No user document found for current user');
        return null;
      }
    } catch (error) {
      logError('Error accessing Firestore', error);
      return null;
    }
  } catch (error) {
    logError('Error getting current user', error);
    return null;
  }
};
export const getUserById = async (uid: string): Promise<UserProfile | null> => {
  try {
    // For demo users, provide mock data when Firebase is not accessible
    if (uid.startsWith('demo-')) {
      try {
        const userDocRef = doc(db, 'users', uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          return userDoc.data() as UserProfile;
        }
      } catch (error) {
        logWarn('Firebase not accessible for demo user, using mock data', error);
      }
      
      // Return mock demo user data
      const mockUser: UserProfile = {
        uid: uid,
        displayName: uid === 'demo-user1' ? 'Demo User 1' : 'Demo User 2',
        email: `${uid}@demo.com`,
        age: 25,
        bio: 'This is a demo user profile for testing purposes.',
        location: { city: 'Demo City', state: 'Demo State' },
        photos: ['https://via.placeholder.com/400x600?text=Demo+Photo'],
        interests: ['Demo Interest 1', 'Demo Interest 2'],
        isVerified: false,
        isPremium: false,
        createdAt: new Date(),
        lastActive: new Date(),
        isOnline: true,
        relationshipStatus: 'single',
        height: '5\'10"',
        profession: 'Demo Professional',
        education: 'Demo University',
        promptResponses: []
      };
      return mockUser;
    }
    
    // Ensure we have authentication for non-demo users
    const token = getAuthToken();
    if (!token) {
      logError('Attempted to get user without authentication');
      throw new Error('Authentication required');
    }
    
    const userDocRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userDocRef);
    if (userDoc.exists()) {
      return userDoc.data() as UserProfile;
    } else {
      logDebug(`No user found with ID: ${uid}`);
      return null;
    }
  } catch (error) {
    logWarn('Error getting user by ID', error);
    return null;
  }
};
export const getUserProfile = async (userId: string): Promise<UserProfile | null> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      return userDoc.data() as UserProfile;
    } else {
      return null;
    }
  } catch (error) {
    logError('Error getting user profile', error);
    throw error;
  }
};
export const createUser = async (userData: UserProfile): Promise<void> => {
  try {
    const userDocRef = doc(db, 'users', userData.uid);

    // Create default profile sections using legacy data
    const defaultSections = createDefaultProfileSections(userData);

    // Enhanced user data with new architecture
    const enhancedUserData: UserProfile = {
      ...userData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      isActive: true,
      lastSeen: serverTimestamp(),
      onboardingComplete: false,
      currentMode: 'dating', // Default to dating mode
      
      // Core shared data
      age: userData.age || 18,
      gender: userData.gender || '',
      location: userData.location || '',
      photos: userData.photos || [],
      
      // New dual-mode sections
      datingProfile: defaultSections.datingProfile,
      nowProfile: defaultSections.nowProfile,
      modeVisibility: defaultSections.modeVisibility,
      
      // Enhanced preferences
      preferences: {
        ageRange: userData.preferences?.ageRange || [18, 50],
        maxDistance: userData.preferences?.maxDistance || 50,
        showMe: userData.preferences?.showMe || 'everyone',
        units: 'imperial'
      },
      
      // Enhanced stats with mode-specific tracking
      stats: {
        likes: 0,
        matches: 0,
        superLikes: 0,
        views: 0,
        messages: 0,
        datingLikes: 0,
        nowTaps: 0,
        ...userData.stats
      }
    };
    
    await setDoc(userDocRef, enhancedUserData);
    logDebug('User created successfully with dual-mode profile');
  } catch (error) {
    logError('Error creating user', error);
    throw error;
  }
};

// FIXED: Use setDoc with merge instead of updateDoc
export const updateUserProfile = async (uid: string, profileData: Partial<UserProfile>): Promise<void> => {
  try {
    const userDocRef = doc(db, 'users', uid);

    // Use setDoc with merge instead of updateDoc
    // This will create the document if it doesn't exist, or update if it does
    await setDoc(userDocRef, {
      ...profileData,
      uid,
      // Ensure the uid is always set
      updatedAt: serverTimestamp()
    }, {
      merge: true
    });
    logDebug('User profile updated successfully');
  } catch (error) {
    logError('Error updating user profile', error);
    throw error;
  }
};

// NEW: Ensure user document exists (for Auth0 users)
export const ensureUserDocument = async (authUser: any): Promise<UserProfile> => {
  try {
    const userId = authUser.sub || authUser.uid; // Auth0 uses 'sub', Firebase uses 'uid'

    // Check if user document already exists
    const existingProfile = await getUserProfile(userId);
    if (!existingProfile) {
      logDebug('Creating new user document for:', authUser.email);

      // Check if email is banned before creating account
      const { banService } = await import('../services/banService');
      const banCheck = await banService.isEmailBanned(authUser.email || '');
      
      if (banCheck.isBanned) {
        const errorMessage = banCheck.banRecord?.expiresAt 
          ? `Your email is banned until ${banCheck.banRecord.expiresAt.toDate().toLocaleDateString()}. Reason: ${banCheck.reason}`
          : `Your email is permanently banned. Reason: ${banCheck.reason}`;
        
        throw new Error(errorMessage);
      }

      // Check beta eligibility and auto-assign if eligible
      const betaEligibility = await checkBetaEligibility();
      let isBetaUser = false;
      let hasLifetimePremium = false;
      
      if (betaEligibility.eligible) {
        logDebug('User is eligible for beta program, auto-assigning...');
        // Auto-assign beta status for eligible users
        try {
          const betaResult = await assignBetaStatus(userId, []); // Empty features initially, will be set during onboarding
          isBetaUser = betaResult.success;
          hasLifetimePremium = betaResult.hasLifetimePremium;
          logDebug(`Beta status assigned: ${isBetaUser}, lifetime premium: ${hasLifetimePremium}`);
        } catch (betaError) {
          logError('Error assigning beta status', betaError);
          // Continue without beta status if assignment fails
        }
      }

      // Generate anonymous ID and create identity mapping
      const { anonymousIdentityService } = await import('../services/anonymousIdentityService');
      const anonymousId = await anonymousIdentityService.createIdentityMapping(userId, authUser.email || '', {
        registrationSource: 'auth_registration'
      });

      // Create basic user document with anonymous ID
      const newUserData: UserProfile = {
        uid: userId,
        anonymousId: anonymousId,
        displayName: authUser.name || authUser.email || 'Unknown User',
        photoURL: authUser.picture || '',
        role: 'free',
        isActive: true,
        onboardingComplete: false,
        // Important: set to false for new users
        // Beta user fields
        isBetaUser: isBetaUser,
        hasLifetimePremium: hasLifetimePremium,
        ...(isBetaUser && { betaJoinedAt: new Date().toISOString() }),
        selectedFeatures: [],
        // Initialize empty arrays/objects
        photos: [],
        interests: [],
        intentions: [],
        prompts: [],
        preferences: {
          ageRange: [18, 50],
          maxDistance: 50,
          showMe: 'everyone'
        },
        stats: {
          likes: 0,
          matches: 0,
          superLikes: 0,
          views: 0,
          messages: 0
        }
      };
      await createUser(newUserData);
      return newUserData;
    } else {
      logDebug('User document already exists');
      
      // Check if existing user has anonymous ID, if not, generate one
      // Skip anonymous ID generation for admin users
      if (!existingProfile.anonymousId && existingProfile.role !== 'admin') {
        logDebug('Adding anonymous ID to existing user');
        try {
          const { anonymousIdentityService } = await import('../services/anonymousIdentityService');
          const anonymousId = await anonymousIdentityService.createIdentityMapping(userId, authUser.email || '', {
            registrationSource: 'existing_user_migration'
          });

          // Update user profile with anonymous ID
          const updatedProfile = { ...existingProfile, anonymousId };
          await updateUserProfile(userId, { anonymousId });

          return updatedProfile;
        } catch (error) {
          logError('Error adding anonymous ID to existing user', error);
          // Continue without anonymous ID for now
          return existingProfile;
        }
      }
      
      return existingProfile;
    }
  } catch (error) {
    logError('Error ensuring user document', error);
    throw error;
  }
};

// NEW: Get all users (for admin)
export const getAllUsers = async (): Promise<UserProfile[]> => {
  try {
    logDebug('Fetching all users...');
    const q = query(usersCollection, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    const users: UserProfile[] = [];
    querySnapshot.forEach(doc => {
      users.push({
        id: doc.id,
        ...doc.data()
      } as UserProfile);
    });
    logDebug(`Found ${users.length} users`);
    return users;
  } catch (error) {
    logError('Error getting all users', error);
    throw error;
  }
};

// NEW: Real-time listener for users (for admin dashboard)
export const listenToUsers = (callback: (users: UserProfile[]) => void) => {
  try {
    const q = query(usersCollection, orderBy('createdAt', 'desc'));
    return onSnapshot(q, 
      snapshot => {
        const users: UserProfile[] = [];
        snapshot.forEach(doc => {
          users.push({
            id: doc.id,
            ...doc.data()
          } as UserProfile);
        });
        callback(users);
      },
      error => {
        logWarn('Firebase users listener permission denied, using empty list', error);
        callback([]); // Return empty array when Firebase is not accessible
      }
    );
  } catch (error) {
    logWarn('Error setting up users listener', error);
    callback([]); // Return empty array when Firebase is not accessible
    return () => {}; // Return dummy unsubscribe function
  }
};

// NEW: Get users for matching
export const getUsersForMatching = async (currentUserId: string, excludeUserIds: string[] = []): Promise<UserProfile[]> => {
  try {
    const q = query(usersCollection, where('isActive', '==', true), orderBy('lastSeen', 'desc'), limit(20));
    const querySnapshot = await getDocs(q);
    const users: UserProfile[] = [];
    querySnapshot.forEach(doc => {
      const userData = {
        id: doc.id,
        ...doc.data()
      } as UserProfile;
      // Exclude current user and users in exclude list
      if (userData.uid !== currentUserId && !excludeUserIds.includes(userData.uid)) {
        users.push(userData);
      }
    });
    return users;
  } catch (error) {
    logError('Error getting users for matching', error);
    throw error;
  }
};

// NEW: Update last seen
export const updateLastSeen = async (userId: string): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      lastSeen: serverTimestamp()
    });
  } catch (error) {
    logError('Error updating last seen', error);
  }
};

// ==================== USER SETTINGS FUNCTIONS ====================

export const getUserSettings = async (uid: string): Promise<UserSettings | null> => {
  try {
    const settingsDocRef = doc(db, 'userSettings', uid);
    const settingsDoc = await getDoc(settingsDocRef);
    if (settingsDoc.exists()) {
      return settingsDoc.data() as UserSettings;
    } else {
      // Create default settings if none exist
      const defaultSettings: UserSettings = {
        uid,
        useMetric: true,
        genderPreferences: [],
        relationshipStatusPreferences: [],
        nowMode: false,
        profileVisibility: 'public',
        showOnlineStatus: true,
        showLastActive: true,
        showReadReceipts: true,
        incognitoMode: false,
        dataSharing: true,
        locationPrecision: 'exact',
        pushNotificationsEnabled: true,
        emailNotificationsEnabled: true,
        inAppNotificationsEnabled: true
      };
      await setDoc(settingsDocRef, {
        ...defaultSettings,
        updatedAt: serverTimestamp()
      });
      return defaultSettings;
    }
  } catch (error) {
    logError('Error getting user settings', error);
    return null;
  }
};
export const updateUserSettings = async (uid: string, settingsData: Partial<UserSettings>): Promise<void> => {
  try {
    const settingsDocRef = doc(db, 'userSettings', uid);
    const settingsDoc = await getDoc(settingsDocRef);
    if (settingsDoc.exists()) {
      await updateDoc(settingsDocRef, {
        ...settingsData,
        updatedAt: serverTimestamp()
      });
    } else {
      await setDoc(settingsDocRef, {
        uid,
        ...settingsData,
        updatedAt: serverTimestamp()
      });
    }
    logDebug('User settings updated successfully');
  } catch (error) {
    logError('Error updating user settings', error);
    throw error;
  }
};

// ==================== APP SETTINGS FUNCTIONS ====================

export const getAppSettings = async (): Promise<AppSettings | null> => {
  try {
    const appSettingsDocRef = doc(db, 'appSettings', 'global');
    const appSettingsDoc = await getDoc(appSettingsDocRef);
    if (appSettingsDoc.exists()) {
      return appSettingsDoc.data() as AppSettings;
    } else {
      // Create default app settings if none exist
      const defaultAppSettings: AppSettings = {
        id: 'global',
        appName: 'FMK',
        maxProfilePhotos: 6,
        maxBioLength: 300,
        maxDistance: 100,
        ageRangeMin: 18,
        ageRangeMax: 99,
        autoModeratePhotos: true,
        autoModerateBios: true,
        reportThreshold: 3,
        enablePushNotifications: true,
        newMatchNotification: true,
        newMessageNotification: true,
        maintenanceMode: false,
        debugMode: false,
        analyticsEnabled: true,
        resendApiKey: 're_123456789',
        resendFromEmail: 'notifications@getFMK.app',
        resendFromName: 'FMK',
        emailTemplateNewMatch: 'template_new_match',
        emailTemplateNewMessage: 'template_new_message',
        emailTemplateWeeklySummary: 'template_weekly_summary',
        auth0Domain: 'thirstyapp.au.auth0.com',
        auth0ClientId: 'jkQACAJ883UYqvCP3dhAFlVtH85aEe1w',
        auth0RedirectUri: window.location.origin,
        auth0Audience: '',
        auth0Scope: 'openid profile email',
        auth0Connection: '',
        auth0PromptLogin: false,
        // Feature settings
        features: {
          unlimited_likes: {
            enabled: true,
            isPremium: true,
            limit: 10
          },
          see_who_likes: {
            enabled: true,
            isPremium: true
          },
          super_likes: {
            enabled: true,
            isPremium: true,
            limit: 3
          },
          rewind: {
            enabled: true,
            isPremium: true,
            limit: 1
          },
          incognito: {
            enabled: true,
            isPremium: true
          },
          message_before_match: {
            enabled: false,
            isPremium: true
          },
          basic_matching: {
            enabled: true,
            isPremium: false
          },
          basic_messaging: {
            enabled: true,
            isPremium: false
          }
        }
      };
      await setDoc(appSettingsDocRef, {
        ...defaultAppSettings,
        updatedAt: serverTimestamp()
      });
      return defaultAppSettings;
    }
  } catch (error) {
    logWarn('Firebase app settings not accessible, using defaults', error);
    // Return default settings when Firebase is not accessible (for demo/dev mode)
    return {
      id: 'global',
      appName: 'FMK',
      maxProfilePhotos: 6,
      maxBioLength: 300,
      maxDistance: 100,
      ageRangeMin: 18,
      ageRangeMax: 99,
      autoModeratePhotos: true,
      autoModerateBios: true,
      reportThreshold: 3,
      enablePushNotifications: true,
      newMatchNotification: true,
      newMessageNotification: true,
      maintenanceMode: false,
      debugMode: false,
      analyticsEnabled: true,
      resendApiKey: 're_123456789',
      resendFromEmail: 'notifications@thirstapp.com',
      resendFromName: 'FMK',
      emailTemplateNewMatch: 'template_new_match',
      emailTemplateNewMessage: 'template_new_message',
      emailTemplateWeeklySummary: 'template_weekly_summary',
      auth0Domain: 'thirstyapp.au.auth0.com',
      auth0ClientId: 'jkQACAJ883UYqvCP3dhAFlVtH85aEe1w',
      auth0RedirectUri: window.location.origin,
      auth0Audience: '',
      auth0Scope: 'openid profile email',
      auth0Connection: '',
      auth0PromptLogin: false,
      features: {
        unlimited_likes: { enabled: true, isPremium: true, limit: 10 },
        see_who_likes: { enabled: true, isPremium: true },
        super_likes: { enabled: true, isPremium: true, limit: 3 },
        rewind: { enabled: true, isPremium: true, limit: 1 },
        incognito: { enabled: true, isPremium: true },
        message_before_match: { enabled: false, isPremium: true },
        basic_matching: { enabled: true, isPremium: false },
        basic_messaging: { enabled: true, isPremium: false }
      }
    };
  }
};
export const updateAppSettings = async (settingsData: Partial<AppSettings>): Promise<void> => {
  try {
    const appSettingsDocRef = doc(db, 'appSettings', 'global');
    const appSettingsDoc = await getDoc(appSettingsDocRef);
    if (appSettingsDoc.exists()) {
      await updateDoc(appSettingsDocRef, {
        ...settingsData,
        updatedAt: serverTimestamp()
      });
    } else {
      await setDoc(appSettingsDocRef, {
        id: 'global',
        ...settingsData,
        updatedAt: serverTimestamp()
      });
    }
    logDebug('App settings updated successfully');
  } catch (error) {
    logError('Error updating app settings', error);
    throw error;
  }
};

// ==================== MATCHING FUNCTIONS ====================

// NEW: Create a like/pass action
export const createLikeAction = async (fromUserId: string, toUserId: string, action: 'like' | 'pass' | 'super_like', isSuper: boolean = false): Promise<boolean> => {
  try {
    const batch = writeBatch(db);

    // Create like document
    const likeRef = doc(likesCollection);
    const likeData: LikeAction = {
      fromUserId,
      toUserId,
      action,
      isSuper,
      createdAt: serverTimestamp() as Timestamp
    };
    batch.set(likeRef, likeData);

    // Update user stats
    if (action === 'like') {
      const fromUserRef = doc(db, 'users', fromUserId);
      batch.update(fromUserRef, {
        'stats.likes': increment(1)
      });
      const toUserRef = doc(db, 'users', toUserId);
      batch.update(toUserRef, {
        'stats.views': increment(1)
      });
    }

    // Check for mutual like (match)
    let isMatch = false;
    if (action === 'like') {
      const mutualLikeQuery = query(likesCollection, where('fromUserId', '==', toUserId), where('toUserId', '==', fromUserId), where('action', '==', 'like'));
      const mutualLikeSnap = await getDocs(mutualLikeQuery);
      if (!mutualLikeSnap.empty) {
        // Create match using your existing function
        await createOrUpdateMatch(fromUserId, toUserId, true);

        // Update match stats
        const fromUserRef = doc(db, 'users', fromUserId);
        const toUserRef = doc(db, 'users', toUserId);
        batch.update(fromUserRef, {
          'stats.matches': increment(1)
        });
        batch.update(toUserRef, {
          'stats.matches': increment(1)
        });
        isMatch = true;
      }
    }
    await batch.commit();
    logDebug('Like action created successfully');
    return isMatch;
  } catch (error) {
    logError('Error creating like action', error);
    throw error;
  }
};

// Enhanced match functions (keeping your existing logic)
export const createOrUpdateMatch = async (userId1: string, userId2: string, isMatched: boolean = false): Promise<string | null> => {
  try {
    // Sort user IDs to ensure consistent document IDs
    const users = [userId1, userId2].sort();
    // Check if match already exists
    const q = query(matchesCollection, where('users', '==', users));
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      // Match exists, update it
      const matchDoc = querySnapshot.docs[0];
      const matchData = matchDoc.data() as Match;
      if (isMatched && !matchData.matchedAt) {
        await updateDoc(doc(db, 'matches', matchDoc.id), {
          matchedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });

        // Create conversation for the match
        await createConversation(users);
      }
      return matchDoc.id;
    } else {
      // Create new match
      const matchData: Match = {
        users,
        createdAt: serverTimestamp() as Timestamp,
        ...(isMatched ? {
          matchedAt: serverTimestamp() as Timestamp
        } : {})
      };
      const matchRef = await addDoc(matchesCollection, matchData);

      // Create conversation if it's a match
      if (isMatched) {
        await createConversation(users);
      }
      return matchRef.id;
    }
  } catch (error) {
    logError('Error creating or updating match', error);
    return null;
  }
};
export const getUserMatches = async (uid: string): Promise<Match[]> => {
  try {
    const q = query(matchesCollection, where('users', 'array-contains', uid), where('matchedAt', '!=', null));
    const querySnapshot = await getDocs(q);
    const matches: Match[] = [];
    querySnapshot.forEach(doc => {
      matches.push({
        id: doc.id,
        ...doc.data()
      } as Match);
    });
    return matches;
  } catch (error) {
    logError('Error getting user matches', error);
    return [];
  }
};

// Enhanced match creation function with FMK support
export const createFMKMatch = async (
  userId1: string,
  userId2: string,
  user1Action: 'fuck' | 'marry',
  user2Action: 'fuck' | 'marry'
): Promise<string | null> => {
  try {
    // Sort user IDs to ensure consistent document IDs
    const users = [userId1, userId2].sort();

    // Determine match type
    let matchType: 'perfect-fuck' | 'perfect-marry' | 'mixed';
    if (user1Action === 'fuck' && user2Action === 'fuck') {
      matchType = 'perfect-fuck';
    } else if (user1Action === 'marry' && user2Action === 'marry') {
      matchType = 'perfect-marry';
    } else {
      matchType = 'mixed';
    }

    logDebug(`Creating FMK match: ${user1Action} + ${user2Action} = ${matchType}`);

    // Check if match already exists
    const q = query(matchesCollection, where('users', '==', users));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      // Match exists, update it
      const matchDoc = querySnapshot.docs[0];
      const matchData = matchDoc.data() as Match;

      if (!matchData.matchedAt) {
        await updateDoc(doc(db, 'matches', matchDoc.id), {
          matchedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          matchType,
          userActions: {
            [userId1]: user1Action,
            [userId2]: user2Action
          }
        });

        // Create conversation for the match with match type metadata
        const conversationId = await createConversation(users);

        // Update conversation with match type
        if (conversationId) {
          const conversationRef = doc(db, 'conversations', conversationId);
          await updateDoc(conversationRef, {
            matchType,
            mode: 'dating' // FMK matches are dating mode
          });
        }

        logDebug('FMK match updated successfully');
      }
      return matchDoc.id;
    } else {
      // Create new match
      const matchData = {
        users,
        createdAt: serverTimestamp() as Timestamp,
        matchedAt: serverTimestamp() as Timestamp,
        matchType,
        userActions: {
          [userId1]: user1Action,
          [userId2]: user2Action
        }
      };

      const matchRef = await addDoc(matchesCollection, matchData);

      // Create conversation with match type metadata
      const conversationId = await createConversation(users);

      if (conversationId) {
        const conversationRef = doc(db, 'conversations', conversationId);
        await updateDoc(conversationRef, {
          matchType,
          mode: 'dating'
        });
      }

      logDebug('FMK match created successfully');
      return matchRef.id;
    }
  } catch (error) {
    logError('Error creating FMK match', error);
    return null;
  }
};

// Get pending selections for a user (users who have chosen them but await response)
export const getPendingSelections = async (currentUserId: string): Promise<{
  fuckSelections: Array<{
    id: string;
    user: UserProfile;
    timestamp: Date;
    seen: boolean;
    action: 'fuck';
  }>;
  marrySelections: Array<{
    id: string;
    user: UserProfile;
    timestamp: Date;
    seen: boolean;
    action: 'marry';
  }>;
}> => {
  try {
    console.log('📋 Loading pending selections for user:', currentUserId);

    // Get all users to check their action arrays
    const usersCollection = collection(db, 'users');
    const usersSnapshot = await getDocs(usersCollection);

    const fuckSelections: any[] = [];
    const marrySelections: any[] = [];

    // Check existing matches to exclude users already matched
    const matchesCollection = collection(db, 'matches');
    const matchQuery = query(
      matchesCollection,
      where('users', 'array-contains', currentUserId)
    );
    const matchesSnapshot = await getDocs(matchQuery);
    const matchedUserIds = new Set<string>();

    matchesSnapshot.docs.forEach(doc => {
      const matchData = doc.data();
      const otherUserId = matchData.users.find((id: string) => id !== currentUserId);
      if (otherUserId) {
        matchedUserIds.add(otherUserId);
      }
    });

    // Check each user's action arrays for selections on current user
    for (const userDoc of usersSnapshot.docs) {
      const userData = userDoc.data();
      const userId = userDoc.id;

      // Skip self and already matched users
      if (userId === currentUserId || matchedUserIds.has(userId)) {
        continue;
      }

      // Check if this user fucked the current user
      const fuckedUsers = userData.fuckedUsers || [];
      const fuckAction = fuckedUsers.find((action: any) => action.userId === currentUserId);
      if (fuckAction) {
        // Sanitize user data to prevent email exposure
        const sanitizedUser = {
          uid: userData.uid,
          displayName: userData.displayName || userData.name || userData.firstName || 'Someone',
          age: userData.age || 0,
          bio: userData.bio || '',
          photos: userData.photos || [],
          photoURL: userData.photoURL,
          // Deliberately exclude email and other sensitive data
        };

        fuckSelections.push({
          id: `fuck-${userId}`,
          user: sanitizedUser as UserProfile,
          timestamp: fuckAction.timestamp ? new Date(fuckAction.timestamp) : new Date(),
          seen: false,
          action: 'fuck' as const
        });
      }

      // Check if this user married the current user
      const marriedUsers = userData.marriedUsers || [];
      const marryAction = marriedUsers.find((action: any) => action.userId === currentUserId);
      if (marryAction) {
        // Sanitize user data to prevent email exposure
        const sanitizedUser = {
          uid: userData.uid,
          displayName: userData.displayName || userData.name || userData.firstName || 'Someone',
          age: userData.age || 0,
          bio: userData.bio || '',
          photos: userData.photos || [],
          photoURL: userData.photoURL,
          // Deliberately exclude email and other sensitive data
        };

        marrySelections.push({
          id: `marry-${userId}`,
          user: sanitizedUser as UserProfile,
          timestamp: marryAction.timestamp ? new Date(marryAction.timestamp) : new Date(),
          seen: false,
          action: 'marry' as const
        });
      }
    }

    // Sort by timestamp (newest first)
    fuckSelections.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    marrySelections.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

    console.log(`✅ Found ${fuckSelections.length} fuck selections, ${marrySelections.length} marry selections`);

    return {
      fuckSelections,
      marrySelections
    };
  } catch (error) {
    console.error('❌ Error loading pending selections:', error);
    return {
      fuckSelections: [],
      marrySelections: []
    };
  }
};

// ==================== CONVERSATION AND MESSAGING FUNCTIONS ====================

// NEW: Create conversation
const createConversation = async (participants: string[]): Promise<string | null> => {
  try {
    // Check if conversation already exists
    const q = query(conversationsCollection, where('participants', '==', participants.sort()));
    const existing = await getDocs(q);
    if (!existing.empty) {
      return existing.docs[0].id;
    }

    // Create new conversation
    const conversationData: Conversation = {
      participants: participants.sort(),
      lastMessage: '',
      lastMessageTimestamp: serverTimestamp() as Timestamp,
      unreadCount: {},
      createdAt: serverTimestamp() as Timestamp,
      updatedAt: serverTimestamp() as Timestamp,
      type: 'matched',
      mode: 'dating',
      isAccepted: true // Matched conversations are automatically accepted
    };
    const conversationRef = await addDoc(conversationsCollection, conversationData);
    return conversationRef.id;
  } catch (error) {
    logError('Error creating conversation', error);
    return null;
  }
};

// Create direct conversation (no matching required)
export const createDirectConversation = async (
  initiatorId: string,
  recipientId: string,
  mode: 'dating' | 'now',
  fromComment?: boolean
): Promise<string | null> => {
  try {
    const participants = [initiatorId, recipientId].sort();
    
    // Check if conversation already exists
    const q = query(conversationsCollection, where('participants', '==', participants));
    const existing = await getDocs(q);
    
    if (!existing.empty) {
      return existing.docs[0].id;
    }

    // Create new direct conversation
    const conversationData: Conversation = {
      participants,
      lastMessage: '',
      lastMessageTimestamp: serverTimestamp() as Timestamp,
      unreadCount: {},
      createdAt: serverTimestamp() as Timestamp,
      updatedAt: serverTimestamp() as Timestamp,
      type: fromComment ? 'comment_reply' : 'direct',
      mode,
      initiatedBy: initiatorId,
      isAccepted: mode === 'now' // Now mode conversations are immediately accepted, Dating mode needs acceptance
    };

    const conversationRef = await addDoc(conversationsCollection, conversationData);
    logDebug(`Direct ${mode} conversation created successfully`);
    return conversationRef.id;
  } catch (error) {
    logError('Error creating direct conversation', error);
    return null;
  }
};

// Accept a direct conversation (for Dating mode)
export const acceptDirectConversation = async (conversationId: string): Promise<boolean> => {
  try {
    const conversationRef = doc(db, 'conversations', conversationId);
    await updateDoc(conversationRef, {
      isAccepted: true,
      updatedAt: serverTimestamp()
    });
    
    console.log('✅ Direct conversation accepted');
    return true;
  } catch (error) {
    console.error('❌ Error accepting direct conversation:', error);
    return false;
  }
};

// Check if user can message another user directly (based on mode and settings)
export const canMessageDirectly = async (
  fromUserId: string,
  toUserId: string,
  mode: 'dating' | 'now'
): Promise<{
  canMessage: boolean;
  requiresAcceptance: boolean;
  reason?: string;
}> => {
  try {
    // Get both users' profiles to check their settings
    const [fromUser, toUser] = await Promise.all([
      getUserById(fromUserId),
      getUserById(toUserId)
    ]);

    if (!fromUser || !toUser) {
      return { canMessage: false, requiresAcceptance: false, reason: 'User not found' };
    }

    // Check if they're in the same mode or if the recipient accepts cross-mode messages
    const fromUserMode = mode;
    const toUserVisibility = toUser.modeVisibility;

    // Now mode - generally more open messaging
    if (mode === 'now') {
      if (!toUserVisibility?.showInNow) {
        return { canMessage: false, requiresAcceptance: false, reason: 'User not active in Now mode' };
      }
      return { canMessage: true, requiresAcceptance: false }; // Now mode is instant
    }

    // Dating mode - may require acceptance
    if (mode === 'dating') {
      if (!toUserVisibility?.showInDating) {
        return { canMessage: false, requiresAcceptance: false, reason: 'User not active in Dating mode' };
      }
      return { canMessage: true, requiresAcceptance: true }; // Dating mode requires acceptance
    }

    return { canMessage: true, requiresAcceptance: mode === 'dating' };
  } catch (error) {
    console.error('❌ Error checking message permissions:', error);
    return { canMessage: false, requiresAcceptance: false, reason: 'Error checking permissions' };
  }
};

export const getUserConversations = async (uid: string): Promise<Conversation[]> => {
  try {
    const q = query(conversationsCollection, where('participants', 'array-contains', uid), orderBy('lastMessageTimestamp', 'desc'));
    const querySnapshot = await getDocs(q);
    const conversations: Conversation[] = [];
    querySnapshot.forEach(doc => {
      conversations.push({
        id: doc.id,
        ...doc.data()
      } as Conversation);
    });
    return conversations;
  } catch (error) {
    console.error('Error getting user conversations:', error);
    return [];
  }
};
export const getConversationMessages = async (conversationId: string): Promise<Message[]> => {
  try {
    const q = query(collection(db, 'messages'), where('conversationId', '==', conversationId), orderBy('timestamp', 'asc'));
    const querySnapshot = await getDocs(q);
    const messages: Message[] = [];
    querySnapshot.forEach(doc => {
      messages.push({
        id: doc.id,
        ...doc.data()
      } as Message);
    });
    return messages;
  } catch (error) {
    console.error('Error getting conversation messages:', error);
    return [];
  }
};
export const sendMessage = async (messageData: Omit<Message, 'id' | 'timestamp'>): Promise<string | null> => {
  try {
    // Add the message
    const messageRef = await addDoc(messagesCollection, {
      ...messageData,
      timestamp: serverTimestamp(),
      read: false
    });

    // Update the conversation with the last message
    const conversationRef = doc(db, 'conversations', messageData.conversationId);
    const conversationDoc = await getDoc(conversationRef);
    if (conversationDoc.exists()) {
      const conversationData = conversationDoc.data() as Conversation;
      const unreadCount = {
        ...conversationData.unreadCount
      };
      // Increment unread count for all participants except sender
      conversationData.participants.forEach(participant => {
        if (participant !== messageData.senderId) {
          unreadCount[participant] = (unreadCount[participant] || 0) + 1;
        }
      });
      await updateDoc(conversationRef, {
        lastMessage: messageData.text,
        lastMessageTimestamp: serverTimestamp(),
        unreadCount,
        updatedAt: serverTimestamp()
      });

      // Update sender's message stats
      const senderRef = doc(db, 'users', messageData.senderId);
      await updateDoc(senderRef, {
        'stats.messages': increment(1)
      });
    }
    return messageRef.id;
  } catch (error) {
    console.error('Error sending message:', error);
    return null;
  }
};

// NEW: Listen to messages in real-time
export const listenToMessages = (conversationId: string, callback: (messages: Message[]) => void) => {
  try {
    const q = query(collection(db, 'messages'), where('conversationId', '==', conversationId), orderBy('timestamp', 'asc'));
    return onSnapshot(q, 
      snapshot => {
        const messages: Message[] = [];
        snapshot.forEach(doc => {
          messages.push({
            id: doc.id,
            ...doc.data()
          } as Message);
        });
        callback(messages);
      },
      error => {
        console.warn('Firebase messages listener permission denied, using empty list:', error);
        callback([]); // Return empty array when Firebase is not accessible
      }
    );
  } catch (error) {
    console.warn('❌ Error setting up messages listener:', error);
    callback([]); // Return empty array when Firebase is not accessible
    return () => {}; // Return dummy unsubscribe function
  }
};

// ==================== ADMIN FUNCTIONS ====================

// NEW: Get user activity for admin
export const getUserActivity = async (userId: string) => {
  try {
    // Get user's likes
    const likesQuery = query(likesCollection, where('fromUserId', '==', userId));
    const likesSnap = await getDocs(likesQuery);

    // Get user's matches
    const matchesQuery = query(matchesCollection, where('users', 'array-contains', userId));
    const matchesSnap = await getDocs(matchesQuery);

    // Get user's messages
    const messagesQuery = query(messagesCollection, where('senderId', '==', userId));
    const messagesSnap = await getDocs(messagesQuery);
    return {
      totalLikes: likesSnap.size,
      totalMatches: matchesSnap.size,
      totalMessages: messagesSnap.size,
      recentActivity: [] // Can be expanded
    };
  } catch (error) {
    console.error('❌ Error getting user activity:', error);
    throw error;
  }
};

// NEW: Create demo data
export const createDemoData = async (): Promise<void> => {
  try {
    console.log('🎭 Creating demo data...');
    console.warn('⚠️ WARNING: This will create demo users in your database. Use only for development!');
    
    const demoUsers: Partial<UserProfile>[] = [{
      uid: 'demo-user-1',
      displayName: 'Sarah Johnson',
      email: 'sarah@example.com',
      age: 25,
      bio: 'Love hiking, coffee, and good conversations 🌟',
      photoURL: 'https://images.unsplash.com/photo-1494790108755-2616b612b3fd?w=400',
      role: 'free',
      interests: ['hiking', 'coffee', 'travel'],
      isActive: true,
      gender: 'woman',
      relationshipStatus: 'single',
      photos: [{
        id: 'demo-photo-1',
        url: 'https://images.unsplash.com/photo-1494790108755-2616b612b3fd?w=400',
        status: 'approved' as const,
        isBlurred: false
      }]
    }, {
      uid: 'demo-user-2',
      displayName: 'Mike Chen',
      email: 'mike@example.com',
      age: 28,
      bio: 'Photographer, foodie, adventure seeker 📸',
      photoURL: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400',
      role: 'premium',
      interests: ['photography', 'food', 'adventure'],
      isActive: true,
      gender: 'man',
      relationshipStatus: 'single',
      photos: [{
        id: 'demo-photo-2',
        url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400',
        status: 'approved' as const,
        isBlurred: false
      }]
    }, {
      uid: 'demo-user-3',
      displayName: 'Emma Wilson',
      email: 'emma@example.com',
      age: 23,
      bio: 'Artist, book lover, yoga enthusiast 🎨',
      photoURL: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400',
      role: 'free',
      interests: ['art', 'books', 'yoga'],
      isActive: true,
      gender: 'woman',
      relationshipStatus: 'single',
      photos: [{
        id: 'demo-photo-3',
        url: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400',
        status: 'approved' as const,
        isBlurred: false
      }]
    }];

    // Create demo users
    for (const user of demoUsers) {
      await createUser(user as UserProfile);
    }
    console.log('✅ Demo data created successfully');
  } catch (error) {
    console.error('❌ Error creating demo data:', error);
    throw error;
  }
};

// Clear demo data from Firebase
export const clearDemoData = async (): Promise<{ success: boolean; deletedUsers: number; errors: string[] }> => {
  try {
    console.log('🧹 Starting demo data cleanup...');
    
    const result = {
      success: true,
      deletedUsers: 0,
      errors: [] as string[]
    };

    const demoUserIds = ['demo-user-1', 'demo-user-2', 'demo-user-3'];
    
    for (const demoUserId of demoUserIds) {
      try {
        console.log(`🗑️ Deleting demo user: ${demoUserId}`);
        
        // Delete from users collection
        const userRef = doc(db, 'users', demoUserId);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          await deleteDoc(userRef);
          result.deletedUsers++;
          console.log(`✅ Deleted user: ${demoUserId}`);
        } else {
          console.log(`ℹ️ User ${demoUserId} not found in database`);
        }
        
        // Delete from userSettings collection
        const settingsRef = doc(db, 'userSettings', demoUserId);
        const settingsDoc = await getDoc(settingsRef);
        if (settingsDoc.exists()) {
          await deleteDoc(settingsRef);
          console.log(`✅ Deleted settings for: ${demoUserId}`);
        }
        
        // Delete related matches
        const matchesQuery = query(
          collection(db, 'matches'),
          where('users', 'array-contains', demoUserId)
        );
        const matchesSnapshot = await getDocs(matchesQuery);
        for (const matchDoc of matchesSnapshot.docs) {
          await deleteDoc(matchDoc.ref);
          console.log(`✅ Deleted match: ${matchDoc.id}`);
        }
        
        // Delete related likes
        const likesQuery = query(
          collection(db, 'likes'),
          where('userId', '==', demoUserId)
        );
        const likesSnapshot = await getDocs(likesQuery);
        for (const likeDoc of likesSnapshot.docs) {
          await deleteDoc(likeDoc.ref);
        }
        
        // Delete likes where this user was liked
        const receivedLikesQuery = query(
          collection(db, 'likes'),
          where('targetUserId', '==', demoUserId)
        );
        const receivedLikesSnapshot = await getDocs(receivedLikesQuery);
        for (const likeDoc of receivedLikesSnapshot.docs) {
          await deleteDoc(likeDoc.ref);
        }
        
        // Delete related messages
        const messagesQuery = query(
          collection(db, 'messages'),
          where('senderId', '==', demoUserId)
        );
        const messagesSnapshot = await getDocs(messagesQuery);
        for (const messageDoc of messagesSnapshot.docs) {
          await deleteDoc(messageDoc.ref);
        }
        
        // Delete conversations where this user is a participant
        const conversationsQuery = query(
          collection(db, 'conversations'),
          where('participants', 'array-contains', demoUserId)
        );
        const conversationsSnapshot = await getDocs(conversationsQuery);
        for (const conversationDoc of conversationsSnapshot.docs) {
          await deleteDoc(conversationDoc.ref);
        }
        
      } catch (error) {
        console.error(`❌ Error deleting demo user ${demoUserId}:`, error);
        result.errors.push(`Failed to delete ${demoUserId}: ${error}`);
        result.success = false;
      }
    }
    
    console.log(`🎉 Demo data cleanup completed. Deleted ${result.deletedUsers} users.`);
    if (result.errors.length > 0) {
      console.warn(`⚠️ Encountered ${result.errors.length} errors:`, result.errors);
    }
    
    return result;
  } catch (error) {
    console.error('❌ Error during demo data cleanup:', error);
    return {
      success: false,
      deletedUsers: 0,
      errors: [`Cleanup failed: ${error}`]
    };
  }
};

// Get database statistics
export const getDatabaseStats = async (): Promise<{
  totalUsers: number;
  demoUsers: number;
  realUsers: number;
  totalMatches: number;
  totalMessages: number;
  totalConversations: number;
}> => {
  try {
    console.log('📊 Gathering database statistics...');
    
    const stats = {
      totalUsers: 0,
      demoUsers: 0,
      realUsers: 0,
      totalMatches: 0,
      totalMessages: 0,
      totalConversations: 0
    };
    
    // Count users
    const usersSnapshot = await getDocs(collection(db, 'users'));
    stats.totalUsers = usersSnapshot.size;
    
    // Count demo vs real users
    usersSnapshot.docs.forEach(doc => {
      const userData = doc.data();
      if (doc.id.startsWith('demo-') || userData.email?.includes('@example.com')) {
        stats.demoUsers++;
      } else {
        stats.realUsers++;
      }
    });
    
    // Count matches
    const matchesSnapshot = await getDocs(collection(db, 'matches'));
    stats.totalMatches = matchesSnapshot.size;
    
    // Count messages
    const messagesSnapshot = await getDocs(collection(db, 'messages'));
    stats.totalMessages = messagesSnapshot.size;
    
    // Count conversations
    const conversationsSnapshot = await getDocs(collection(db, 'conversations'));
    stats.totalConversations = conversationsSnapshot.size;
    
    console.log('📊 Database stats:', stats);
    return stats;
    
  } catch (error) {
    console.error('❌ Error gathering database stats:', error);
    return {
      totalUsers: 0,
      demoUsers: 0,
      realUsers: 0,
      totalMatches: 0,
      totalMessages: 0,
      totalConversations: 0
    };
  }
};

// ==================== UTILITY FUNCTIONS ====================

// Helper function to update user's last active timestamp with optional location
export const updateUserLastActive = async (uid: string, coordinates?: { lat: number; lng: number }): Promise<void> => {
  try {
    const userSettingsRef = doc(db, 'userSettings', uid);
    await setDoc(userSettingsRef, {
      lastActive: serverTimestamp()
    }, { merge: true });

    // If coordinates provided and user has consented to location sharing, store for admin heatmap
    if (coordinates && await hasLocationConsent(uid)) {
      await updateAdminLocationData(uid, coordinates);
    }
  } catch (error) {
    console.error('Error updating user last active:', error);
  }
};

// Helper function to check if user has consented to location sharing
const hasLocationConsent = async (uid: string): Promise<boolean> => {
  try {
    const userSettingsDoc = await getDoc(doc(db, 'userSettings', uid));
    const settings = userSettingsDoc.data();
    return settings?.locationSharingConsent === true;
  } catch (error) {
    console.error('Error checking location consent:', error);
    return false;
  }
};

// Helper function to fuzz coordinates for privacy
const fuzzCoordinates = (lat: number, lng: number): { fuzzedLat: number; fuzzedLng: number } => {
  // Add random offset within ~500m-1km radius for privacy
  const radiusKm = 0.5 + (Math.random() * 0.5); // 0.5-1km radius
  const angle = Math.random() * 2 * Math.PI;

  // Convert to lat/lng offset (approximate)
  const latOffset = (radiusKm / 111.32) * Math.cos(angle); // 111.32 km per degree latitude
  const lngOffset = (radiusKm / (111.32 * Math.cos(lat * Math.PI / 180))) * Math.sin(angle);

  return {
    fuzzedLat: lat + latOffset,
    fuzzedLng: lng + lngOffset
  };
};

// Helper function to update admin location data for heatmap
const updateAdminLocationData = async (uid: string, coordinates: { lat: number; lng: number }): Promise<void> => {
  try {
    const anonymousId = getCurrentUserAnonymousId();
    if (!anonymousId) return;

    const fuzzedCoords = fuzzCoordinates(coordinates.lat, coordinates.lng);
    const now = new Date();
    const ttl = new Date(now.getTime() + (60 * 60 * 1000)); // 1 hour TTL

    // Store anonymous data for general heatmap
    const locationData = {
      anonymousId,
      fuzzedLat: fuzzedCoords.fuzzedLat,
      fuzzedLng: fuzzedCoords.fuzzedLng,
      timestamp: serverTimestamp(),
      isActive: true,
      ttl: Timestamp.fromDate(ttl)
    };

    // Store with anonymousId as document ID to prevent duplicates
    const locationRef = doc(db, 'admin_user_locations', anonymousId);
    await setDoc(locationRef, locationData, { merge: true });

    // Check for compliance violations (sensitive areas)
    await checkComplianceViolations(uid, coordinates, anonymousId);

  } catch (error) {
    console.error('Error updating admin location data:', error);
  }
};

// Helper function to check for compliance violations
const checkComplianceViolations = async (uid: string, coordinates: { lat: number; lng: number }, anonymousId: string): Promise<void> => {
  try {
    // Get geofences for sensitive areas
    const geofencesSnapshot = await getDocs(collection(db, 'admin_geofences'));
    const violations: string[] = [];

    geofencesSnapshot.forEach((geofenceDoc) => {
      const geofence = geofenceDoc.data();
      if (geofence.isActive && isWithinGeofence(coordinates, geofence)) {
        violations.push(geofence.name);
      }
    });

    // If violations found, create secure audit log with user identification
    if (violations.length > 0) {
      const complianceLog = {
        userId: uid, // Real user ID for compliance tracking
        anonymousId, // Anonymous ID for correlation
        coordinates: {
          lat: coordinates.lat,
          lng: coordinates.lng
        },
        violations,
        timestamp: serverTimestamp(),
        severity: determineSeverity(violations),
        status: 'flagged',
        reviewRequired: true
      };

      await addDoc(collection(db, 'admin_compliance_logs'), complianceLog);

      // Log compliance violation for immediate admin attention
      console.warn(`🚨 COMPLIANCE VIOLATION: User ${uid} near sensitive areas: ${violations.join(', ')}`);
    }

  } catch (error) {
    console.error('Error checking compliance violations:', error);
  }
};

// Helper function to check if coordinates are within a geofence
const isWithinGeofence = (coordinates: { lat: number; lng: number }, geofence: any): boolean => {
  const distance = calculateDistance(
    coordinates.lat,
    coordinates.lng,
    geofence.centerLat,
    geofence.centerLng
  );
  return distance <= (geofence.radiusMeters / 1000); // Convert meters to km
};

// Helper function to calculate distance between two points (Haversine formula)
const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Helper function to determine violation severity
const determineSeverity = (violations: string[]): 'low' | 'medium' | 'high' | 'critical' => {
  const criticalKeywords = ['school', 'elementary', 'middle school', 'high school', 'daycare', 'playground'];
  const highKeywords = ['church', 'community center', 'library', 'park'];

  for (const violation of violations) {
    const lowerViolation = violation.toLowerCase();
    if (criticalKeywords.some(keyword => lowerViolation.includes(keyword))) {
      return 'critical';
    }
    if (highKeywords.some(keyword => lowerViolation.includes(keyword))) {
      return 'high';
    }
  }

  return violations.length > 2 ? 'medium' : 'low';
};

// Helper function to check if Firestore is available
export const isFirestoreAvailable = async (): Promise<boolean> => {
  try {
    // Try to access a document to check if Firestore is working
    const testRef = doc(db, 'test', 'connection-test');
    await getDoc(testRef);
    return true;
  } catch (error) {
    console.error('Firestore connection test failed:', error);
    return false;
  }
};

// NEW: Image upload utilities
export const uploadImage = async (file: File, path: string): Promise<string> => {
  try {
    const imageRef = ref(storage, path);
    const snapshot = await uploadBytes(imageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  } catch (error) {
    console.error('❌ Error uploading image:', error);
    throw error;
  }
};
export const deleteImage = async (imagePath: string): Promise<void> => {
  try {
    const imageRef = ref(storage, imagePath);
    await deleteObject(imageRef);
    console.log('✅ Image deleted successfully');
  } catch (error) {
    console.error('❌ Error deleting image:', error);
    throw error;
  }
};

// ==================== DUAL MODE PROFILE FUNCTIONS ====================

/**
 * Serves a profile based on the viewer's current mode
 * This is the core function that determines what profile data to show
 */
export const serveProfileForMode = (
  userProfile: UserProfile, 
  viewerMode: 'dating' | 'now',
  distance?: string
): ServedProfile | null => {
  
  // Check if user allows visibility in this mode
  const visibility = userProfile.modeVisibility;
  if (viewerMode === 'dating' && !visibility.showInDating) return null;
  if (viewerMode === 'now' && !visibility.showInNow) return null;
  
  // Get the appropriate photos for this mode
  let modePhotos: PhotoData[] = [];
  if (viewerMode === 'dating') {
    modePhotos = userProfile.photos.filter(photo => 
      userProfile.datingProfile.selectedPhotos.includes(photo.id) && 
      photo.status === 'approved'
    );
  } else {
    modePhotos = userProfile.photos.filter(photo => 
      userProfile.nowProfile.selectedPhotos.includes(photo.id) && 
      photo.status === 'approved'
    );
  }
  
  // Must have at least one photo to show profile
  if (modePhotos.length === 0) return null;
  
  const baseProfile: ServedProfile = {
    uid: userProfile.uid,
    displayName: userProfile.displayName,
    age: userProfile.age,
    location: userProfile.location,
    photos: modePhotos,
    distance,
    isOnline: userProfile.isActive,
    lastSeen: userProfile.lastSeen,
    mode: viewerMode
  };
  
  // Add mode-specific data
  if (viewerMode === 'dating') {
    baseProfile.datingData = {
      bio: userProfile.datingProfile.bio,
      prompts: userProfile.datingProfile.prompts,
      relationshipGoals: userProfile.datingProfile.relationshipGoals,
      interests: userProfile.datingProfile.interests,
      lifestyle: userProfile.datingProfile.lifestyle
    };
  } else {
    baseProfile.nowData = {
      quickStats: userProfile.nowProfile.quickStats,
      aboutMe: userProfile.nowProfile.aboutMe,
      lookingFor: userProfile.nowProfile.lookingFor,
      availability: userProfile.nowProfile.availability,
      rightNow: userProfile.nowProfile.rightNow,
      tribes: userProfile.nowProfile.tribes
    };
  }
  
  return baseProfile;
};

/**
 * Creates default profile sections for new users
 */
export const createDefaultProfileSections = (userProfile: Partial<UserProfile>): {
  datingProfile: DatingProfile,
  nowProfile: NowProfile,
  modeVisibility: ModeVisibility
} => {
  return {
    datingProfile: {
      selectedPhotos: [],
      prompts: [],
      bio: userProfile.bio || '',
      relationshipGoals: userProfile.intentions || [],
      lookingFor: [],
      interests: userProfile.interests || [],
      values: [],
      dealBreakers: [],
      lifestyle: {}
    },
    nowProfile: {
      selectedPhotos: [],
      quickStats: {
        build: '',
        height: ''
      },
      lookingFor: [],
      availability: 'offline',
      rightNow: false,
      aboutMe: '',
      tribes: [],
      kinks: [],
      safetyInfo: {}
    },
    modeVisibility: {
      showInDating: true,
      showInNow: false, // Default to private for now mode
      datingVisibleToAll: true,
      nowVisibleToAll: false,
      hideFromMatches: false
    }
  };
};

/**
 * Updates a user's profile with mode-specific sections
 */
export const updateProfileSections = async (
  uid: string,
  updates: {
    datingProfile?: Partial<DatingProfile>;
    nowProfile?: Partial<NowProfile>;
    modeVisibility?: Partial<ModeVisibility>;
  }
): Promise<void> => {
  try {
    const userRef = doc(db, 'users', uid);
    const updateData: any = {
      updatedAt: serverTimestamp()
    };
    
    if (updates.datingProfile) {
      // Update specific dating profile fields
      Object.keys(updates.datingProfile).forEach(key => {
        updateData[`datingProfile.${key}`] = updates.datingProfile![key as keyof DatingProfile];
      });
    }
    
    if (updates.nowProfile) {
      // Update specific now profile fields
      Object.keys(updates.nowProfile).forEach(key => {
        updateData[`nowProfile.${key}`] = updates.nowProfile![key as keyof NowProfile];
      });
    }
    
    if (updates.modeVisibility) {
      // Update visibility settings
      Object.keys(updates.modeVisibility).forEach(key => {
        updateData[`modeVisibility.${key}`] = updates.modeVisibility![key as keyof ModeVisibility];
      });
    }
    
    await updateDoc(userRef, updateData);
    console.log('✅ Profile sections updated successfully');
  } catch (error) {
    console.error('❌ Error updating profile sections:', error);
    throw error;
  }
};

// ================================
// Profile Element Comment System (Hinge-style)
// ================================

// Send a comment on a specific profile element
export const sendProfileElementComment = async (
  toUserId: string,
  elementType: 'photo' | 'prompt' | 'bio' | 'quickStats' | 'interests',
  comment: string,
  mode: 'dating' | 'now',
  elementId?: string,
  elementIndex?: number
): Promise<string> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('User not authenticated');

    const interactionData: Partial<ProfileInteraction> = {
      fromUserId: currentUser.uid,
      toUserId,
      type: 'comment',
      mode,
      elementType,
      elementId,
      elementIndex,
      comment,
      createdAt: serverTimestamp(),
      status: 'pending'
    };

    const interactionRef = await addDoc(collection(db, 'profile_interactions'), interactionData);
    
    // Also create a separate comment record for easier querying
    const commentData: Partial<ProfileElementComment> = {
      fromUserId: currentUser.uid,
      toUserId,
      elementType,
      elementId,
      elementIndex,
      comment,
      createdAt: serverTimestamp(),
      isRead: false
    };

    const commentRef = await addDoc(collection(db, 'profile_comments'), commentData);
    
    console.log('✅ Profile element comment sent successfully');
    return commentRef.id;
  } catch (error) {
    console.error('❌ Error sending profile element comment:', error);
    throw error;
  }
};

// Get comments for a specific profile element
export const getProfileElementComments = async (
  userId: string,
  elementType: 'photo' | 'prompt' | 'bio' | 'quickStats' | 'interests',
  elementId?: string,
  elementIndex?: number
): Promise<ProfileElementComment[]> => {
  try {
    let commentsQuery = query(
      collection(db, 'profile_comments'),
      where('toUserId', '==', userId),
      where('elementType', '==', elementType),
      orderBy('createdAt', 'desc')
    );

    if (elementId) {
      commentsQuery = query(commentsQuery, where('elementId', '==', elementId));
    }
    if (elementIndex !== undefined) {
      commentsQuery = query(commentsQuery, where('elementIndex', '==', elementIndex));
    }

    const querySnapshot = await getDocs(commentsQuery);
    const comments = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as ProfileElementComment));

    return comments;
  } catch (error) {
    console.error('❌ Error getting profile element comments:', error);
    return [];
  }
};

// Get all comments received by a user (for notifications)
export const getUserReceivedComments = async (userId: string, unreadOnly: boolean = false): Promise<ProfileElementComment[]> => {
  try {
    let commentsQuery = query(
      collection(db, 'profile_comments'),
      where('toUserId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    if (unreadOnly) {
      commentsQuery = query(commentsQuery, where('isRead', '==', false));
    }

    const querySnapshot = await getDocs(commentsQuery);
    const comments = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as ProfileElementComment));

    return comments;
  } catch (error) {
    console.error('❌ Error getting user received comments:', error);
    return [];
  }
};

// Mark comments as read
export const markCommentsAsRead = async (commentIds: string[]): Promise<void> => {
  try {
    const batch = writeBatch(db);
    
    commentIds.forEach(commentId => {
      const commentRef = doc(db, 'profile_comments', commentId);
      batch.update(commentRef, { isRead: true });
    });

    await batch.commit();
    console.log('✅ Comments marked as read');
  } catch (error) {
    console.error('❌ Error marking comments as read:', error);
    throw error;
  }
};

// Like a specific profile element (alternative to commenting)
export const likeProfileElement = async (
  toUserId: string,
  elementType: 'photo' | 'prompt' | 'bio' | 'quickStats' | 'interests',
  mode: 'dating' | 'now',
  elementId?: string,
  elementIndex?: number
): Promise<string> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('User not authenticated');

    const interactionData: Partial<ProfileInteraction> = {
      fromUserId: currentUser.uid,
      toUserId,
      type: 'like',
      mode,
      elementType,
      elementId,
      elementIndex,
      createdAt: serverTimestamp(),
      status: 'pending'
    };

    const interactionRef = await addDoc(collection(db, 'profile_interactions'), interactionData);
    
    console.log('✅ Profile element liked successfully');
    return interactionRef.id;
  } catch (error) {
    console.error('❌ Error liking profile element:', error);
    throw error;
  }
};

// Get interactions for a profile element (likes + comments)
export const getProfileElementInteractions = async (
  userId: string,
  elementType: 'photo' | 'prompt' | 'bio' | 'quickStats' | 'interests',
  elementId?: string,
  elementIndex?: number
): Promise<ProfileInteraction[]> => {
  try {
    let interactionsQuery = query(
      collection(db, 'profile_interactions'),
      where('toUserId', '==', userId),
      where('elementType', '==', elementType),
      orderBy('createdAt', 'desc')
    );

    if (elementId) {
      interactionsQuery = query(interactionsQuery, where('elementId', '==', elementId));
    }
    if (elementIndex !== undefined) {
      interactionsQuery = query(interactionsQuery, where('elementIndex', '==', elementIndex));
    }

    const querySnapshot = await getDocs(interactionsQuery);
    const interactions = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as ProfileInteraction));

    return interactions;
  } catch (error) {
    console.error('❌ Error getting profile element interactions:', error);
    return [];
  }
};

// ================================
// Cruising Spots System (Sniffies-style)
// ================================

// Collection references for cruising spots
const cruisingSpotsCollection = collection(db, 'cruising_spots');
const userLocationsCollection = collection(db, 'user_locations');
const spotCheckInsCollection = collection(db, 'spot_checkins');

// Create a new cruising spot
export const createCruisingSpot = async (spotData: Omit<CruisingSpot, 'id' | 'createdAt' | 'updatedAt'>): Promise<string | null> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('User not authenticated');

    const newSpotData: Omit<CruisingSpot, 'id'> = {
      ...spotData,
      createdBy: currentUser.uid,
      createdAt: serverTimestamp() as Timestamp,
      updatedAt: serverTimestamp() as Timestamp,
      activeUsers: 0,
      recentActivity: serverTimestamp() as Timestamp,
      moderationStatus: 'pending'
    };

    const spotRef = await addDoc(cruisingSpotsCollection, newSpotData);
    console.log('✅ Cruising spot created successfully');
    return spotRef.id;
  } catch (error) {
    console.error('❌ Error creating cruising spot:', error);
    return null;
  }
};

// Get nearby cruising spots
export const getNearbySpots = async (
  latitude: number,
  longitude: number,
  radiusKm: number = 10,
  filter?: {
    type?: string[];
    activeOnly?: boolean;
    verifiedOnly?: boolean;
  }
): Promise<CruisingSpot[]> => {
  try {
    // Note: In a production app, you'd use GeoHash or similar for efficient geo queries
    // For now, we'll get all spots and filter in memory
    let spotsQuery = query(cruisingSpotsCollection, where('moderationStatus', '==', 'approved'));
    
    if (filter?.activeOnly) {
      spotsQuery = query(spotsQuery, where('isActive', '==', true));
    }
    
    if (filter?.verifiedOnly) {
      spotsQuery = query(spotsQuery, where('isVerified', '==', true));
    }

    const querySnapshot = await getDocs(spotsQuery);
    const spots: CruisingSpot[] = [];
    
    querySnapshot.forEach(doc => {
      const spotData = { id: doc.id, ...doc.data() } as CruisingSpot;
      
      // Calculate distance (simple approximation)
      const spotLat = spotData.latitude;
      const spotLng = spotData.longitude;
      const distance = calculateDistance(latitude, longitude, spotLat, spotLng);
      
      if (distance <= radiusKm) {
        // Filter by type if specified
        if (!filter?.type || filter.type.includes(spotData.type)) {
          spots.push(spotData);
        }
      }
    });

    // Sort by distance and recent activity
    spots.sort((a, b) => {
      const distA = calculateDistance(latitude, longitude, a.latitude, a.longitude);
      const distB = calculateDistance(latitude, longitude, b.latitude, b.longitude);
      return distA - distB;
    });

    return spots;
  } catch (error) {
    console.error('❌ Error getting nearby spots:', error);
    return [];
  }
};

// Update user location for cruising map
export const updateUserLocation = async (locationData: Omit<UserLocationUpdate, 'lastUpdated'>): Promise<boolean> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('User not authenticated');

    const userLocationData: UserLocationUpdate = {
      ...locationData,
      userId: currentUser.uid,
      lastUpdated: serverTimestamp() as Timestamp
    };

    // Use user ID as document ID to ensure one location per user
    const locationRef = doc(userLocationsCollection, currentUser.uid);
    await setDoc(locationRef, userLocationData, { merge: true });
    
    console.log('✅ User location updated');
    return true;
  } catch (error) {
    console.error('❌ Error updating user location:', error);
    return false;
  }
};

// Get active users near a location
export const getNearbyActiveUsers = async (
  latitude: number,
  longitude: number,
  radiusKm: number = 5
): Promise<UserLocationUpdate[]> => {
  try {
    // Get users active in the last 30 minutes
    const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000);
    
    const usersQuery = query(
      userLocationsCollection,
      where('isActive', '==', true),
      where('mode', '==', 'now'),
      where('lastUpdated', '>', Timestamp.fromDate(thirtyMinutesAgo))
    );

    const querySnapshot = await getDocs(usersQuery);
    const nearbyUsers: UserLocationUpdate[] = [];
    
    querySnapshot.forEach(doc => {
      const userData = doc.data() as UserLocationUpdate;
      
      // Calculate distance
      const distance = calculateDistance(latitude, longitude, userData.latitude, userData.longitude);
      
      if (distance <= radiusKm) {
        nearbyUsers.push(userData);
      }
    });

    return nearbyUsers;
  } catch (error) {
    console.error('❌ Error getting nearby users:', error);
    return [];
  }
};

// Check into a cruising spot
export const checkIntoSpot = async (spotId: string, checkInData: Omit<SpotCheckIn, 'id' | 'userId' | 'spotId' | 'checkedInAt'>): Promise<string | null> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('User not authenticated');

    const checkIn: Omit<SpotCheckIn, 'id'> = {
      ...checkInData,
      userId: currentUser.uid,
      spotId,
      checkedInAt: serverTimestamp() as Timestamp,
      status: 'active'
    };

    const checkInRef = await addDoc(spotCheckInsCollection, checkIn);
    
    // Update spot's active user count
    const spotRef = doc(cruisingSpotsCollection, spotId);
    await updateDoc(spotRef, {
      activeUsers: increment(1),
      recentActivity: serverTimestamp(),
      isActive: true
    });
    
    console.log('✅ Checked into cruising spot');
    return checkInRef.id;
  } catch (error) {
    console.error('❌ Error checking into spot:', error);
    return null;
  }
};

// Check out of a cruising spot
export const checkOutOfSpot = async (spotId: string): Promise<boolean> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('User not authenticated');

    // Find user's active check-in for this spot
    const checkInsQuery = query(
      spotCheckInsCollection,
      where('userId', '==', currentUser.uid),
      where('spotId', '==', spotId),
      where('status', '==', 'active')
    );

    const querySnapshot = await getDocs(checkInsQuery);
    
    if (!querySnapshot.empty) {
      const checkInDoc = querySnapshot.docs[0];
      await updateDoc(doc(spotCheckInsCollection, checkInDoc.id), {
        status: 'left'
      });

      // Update spot's active user count
      const spotRef = doc(cruisingSpotsCollection, spotId);
      await updateDoc(spotRef, {
        activeUsers: increment(-1),
        recentActivity: serverTimestamp()
      });
    }
    
    console.log('✅ Checked out of cruising spot');
    return true;
  } catch (error) {
    console.error('❌ Error checking out of spot:', error);
    return false;
  }
};

// Rate a cruising spot
export const rateCruisingSpot = async (spotId: string, rating: number, safetyRating: number): Promise<boolean> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('User not authenticated');

    // In a real app, you'd track individual ratings to prevent duplicate ratings
    const spotRef = doc(cruisingSpotsCollection, spotId);
    const spotDoc = await getDoc(spotRef);
    
    if (spotDoc.exists()) {
      const spotData = spotDoc.data() as CruisingSpot;
      const newTotalRatings = spotData.totalRatings + 1;
      const newAverageRating = ((spotData.averageRating * spotData.totalRatings) + rating) / newTotalRatings;
      const newSafetyRating = ((spotData.safetyRating * spotData.totalRatings) + safetyRating) / newTotalRatings;
      
      await updateDoc(spotRef, {
        averageRating: newAverageRating,
        safetyRating: newSafetyRating,
        totalRatings: newTotalRatings,
        updatedAt: serverTimestamp()
      });
    }
    
    console.log('✅ Cruising spot rated successfully');
    return true;
  } catch (error) {
    console.error('❌ Error rating cruising spot:', error);
    return false;
  }
};

// Report a cruising spot
export const reportCruisingSpot = async (spotId: string, reason: string): Promise<boolean> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('User not authenticated');

    const spotRef = doc(cruisingSpotsCollection, spotId);
    await updateDoc(spotRef, {
      reports: increment(1),
      moderationStatus: 'flagged',
      updatedAt: serverTimestamp()
    });
    
    // In a real app, you'd also create a report record with details
    
    console.log('✅ Cruising spot reported');
    return true;
  } catch (error) {
    console.error('❌ Error reporting cruising spot:', error);
    return false;
  }
};


// ================================
// Group Events/Meetups System (Now Mode)
// ================================

// Collection references for group events
const groupEventsCollection = collection(db, 'group_events');
const eventAttendeesCollection = collection(db, 'event_attendees');
const eventCommentsCollection = collection(db, 'event_comments');

// Create a new group event
export const createGroupEvent = async (eventData: Omit<GroupEvent, 'id' | 'createdAt' | 'updatedAt' | 'hostId' | 'hostName' | 'hostPhotoURL'>): Promise<string | null> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('User not authenticated');

    // Get host user info
    const hostProfile = await getUserById(currentUser.uid);
    if (!hostProfile) throw new Error('Host profile not found');

    const newEventData: Omit<GroupEvent, 'id'> = {
      ...eventData,
      hostId: currentUser.uid,
      hostName: hostProfile.displayName,
      hostPhotoURL: hostProfile.photoURL,
      currentAttendees: 1, // Host is automatically attending
      attendeeIds: [currentUser.uid],
      interestedIds: [],
      createdAt: serverTimestamp() as Timestamp,
      updatedAt: serverTimestamp() as Timestamp,
      moderationStatus: 'pending',
      reports: 0
    };

    const eventRef = await addDoc(groupEventsCollection, newEventData);

    // Create host's attendee record
    const hostAttendeeData: Omit<EventAttendee, 'id'> = {
      eventId: eventRef.id,
      userId: currentUser.uid,
      status: 'going',
      joinedAt: serverTimestamp() as Timestamp,
      rsvpedAt: serverTimestamp() as Timestamp
    };

    await addDoc(eventAttendeesCollection, hostAttendeeData);

    console.log('✅ Group event created successfully');
    return eventRef.id;
  } catch (error) {
    console.error('❌ Error creating group event:', error);
    return null;
  }
};

// Get nearby events
export const getNearbyEvents = async (
  latitude: number,
  longitude: number,
  radiusKm: number = 25,
  filter?: {
    type?: string[];
    category?: string[];
    dateRange?: { start: Date; end: Date };
    maxAttendees?: number;
  }
): Promise<GroupEvent[]> => {
  try {
    let eventsQuery = query(
      groupEventsCollection,
      where('moderationStatus', '==', 'approved'),
      where('status', '==', 'upcoming'),
      orderBy('dateTime', 'asc')
    );

    const querySnapshot = await getDocs(eventsQuery);
    const events: GroupEvent[] = [];
    
    querySnapshot.forEach(doc => {
      const eventData = { id: doc.id, ...doc.data() } as GroupEvent;
      
      // Calculate distance
      const distance = calculateDistance(
        latitude, 
        longitude, 
        eventData.location.latitude, 
        eventData.location.longitude
      );
      
      if (distance <= radiusKm) {
        // Apply filters
        if (filter?.type && !filter.type.includes(eventData.type)) return;
        if (filter?.category && !filter.category.includes(eventData.category)) return;
        if (filter?.maxAttendees && eventData.currentAttendees > filter.maxAttendees) return;
        
        events.push(eventData);
      }
    });

    return events;
  } catch (error) {
    console.error('❌ Error getting nearby events:', error);
    return [];
  }
};

// RSVP to an event
export const rsvpToEvent = async (eventId: string, status: 'going' | 'interested' | 'declined'): Promise<boolean> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('User not authenticated');

    // Check if user already has an RSVP
    const existingRsvpQuery = query(
      eventAttendeesCollection,
      where('eventId', '==', eventId),
      where('userId', '==', currentUser.uid)
    );

    const existingRsvp = await getDocs(existingRsvpQuery);
    
    if (!existingRsvp.empty) {
      // Update existing RSVP
      const rsvpDoc = existingRsvp.docs[0];
      const oldStatus = rsvpDoc.data().status;
      
      await updateDoc(doc(eventAttendeesCollection, rsvpDoc.id), {
        status,
        rsvpedAt: serverTimestamp()
      });

      // Update event attendee counts
      const eventRef = doc(groupEventsCollection, eventId);
      const eventDoc = await getDoc(eventRef);
      
      if (eventDoc.exists()) {
        const eventData = eventDoc.data() as GroupEvent;
        let attendeeUpdate: any = {};
        let interestedUpdate: any = {};

        // Remove from old lists
        if (oldStatus === 'going') {
          attendeeUpdate.attendeeIds = arrayRemove(currentUser.uid);
          attendeeUpdate.currentAttendees = increment(-1);
        } else if (oldStatus === 'interested') {
          interestedUpdate.interestedIds = arrayRemove(currentUser.uid);
        }

        // Add to new lists
        if (status === 'going') {
          attendeeUpdate.attendeeIds = arrayUnion(currentUser.uid);
          attendeeUpdate.currentAttendees = increment(1);
        } else if (status === 'interested') {
          interestedUpdate.interestedIds = arrayUnion(currentUser.uid);
        }

        await updateDoc(eventRef, {
          ...attendeeUpdate,
          ...interestedUpdate,
          updatedAt: serverTimestamp()
        });
      }
    } else {
      // Create new RSVP
      const attendeeData: Omit<EventAttendee, 'id'> = {
        eventId,
        userId: currentUser.uid,
        status,
        joinedAt: serverTimestamp() as Timestamp,
        rsvpedAt: serverTimestamp() as Timestamp
      };

      await addDoc(eventAttendeesCollection, attendeeData);

      // Update event counts
      const eventRef = doc(groupEventsCollection, eventId);
      let updateData: any = { updatedAt: serverTimestamp() };

      if (status === 'going') {
        updateData.attendeeIds = arrayUnion(currentUser.uid);
        updateData.currentAttendees = increment(1);
      } else if (status === 'interested') {
        updateData.interestedIds = arrayUnion(currentUser.uid);
      }

      await updateDoc(eventRef, updateData);
    }

    console.log('✅ RSVP updated successfully');
    return true;
  } catch (error) {
    console.error('❌ Error updating RSVP:', error);
    return false;
  }
};

// Get user's RSVP status for an event
export const getUserRsvpStatus = async (eventId: string): Promise<'going' | 'interested' | 'declined' | null> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return null;

    const rsvpQuery = query(
      eventAttendeesCollection,
      where('eventId', '==', eventId),
      where('userId', '==', currentUser.uid)
    );

    const rsvpDoc = await getDocs(rsvpQuery);
    
    if (!rsvpDoc.empty) {
      return rsvpDoc.docs[0].data().status;
    }
    
    return null;
  } catch (error) {
    console.error('❌ Error getting RSVP status:', error);
    return null;
  }
};

// Get event attendees
export const getEventAttendees = async (eventId: string): Promise<EventAttendee[]> => {
  try {
    const attendeesQuery = query(
      eventAttendeesCollection,
      where('eventId', '==', eventId),
      where('status', 'in', ['going', 'interested'])
    );

    const querySnapshot = await getDocs(attendeesQuery);
    const attendees: EventAttendee[] = [];
    
    querySnapshot.forEach(doc => {
      attendees.push({ id: doc.id, ...doc.data() } as EventAttendee);
    });

    return attendees;
  } catch (error) {
    console.error('❌ Error getting event attendees:', error);
    return [];
  }
};

// Add comment to event
export const addEventComment = async (
  eventId: string,
  content: string,
  type: 'comment' | 'question' | 'update' = 'comment'
): Promise<string | null> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('User not authenticated');

    const userProfile = await getUserById(currentUser.uid);
    if (!userProfile) throw new Error('User profile not found');

    // Check if user is host
    const eventDoc = await getDoc(doc(groupEventsCollection, eventId));
    const isFromHost = eventDoc.exists() && eventDoc.data()?.hostId === currentUser.uid;

    const commentData: Omit<EventComment, 'id'> = {
      eventId,
      userId: currentUser.uid,
      userName: userProfile.displayName,
      userPhotoURL: userProfile.photoURL,
      content: content.trim(),
      type,
      createdAt: serverTimestamp() as Timestamp,
      isFromHost,
      isPinned: false
    };

    const commentRef = await addDoc(eventCommentsCollection, commentData);
    
    console.log('✅ Event comment added successfully');
    return commentRef.id;
  } catch (error) {
    console.error('❌ Error adding event comment:', error);
    return null;
  }
};

// Get event comments
export const getEventComments = async (eventId: string): Promise<EventComment[]> => {
  try {
    const commentsQuery = query(
      eventCommentsCollection,
      where('eventId', '==', eventId),
      orderBy('createdAt', 'asc')
    );

    const querySnapshot = await getDocs(commentsQuery);
    const comments: EventComment[] = [];
    
    querySnapshot.forEach(doc => {
      comments.push({ id: doc.id, ...doc.data() } as EventComment);
    });

    return comments;
  } catch (error) {
    console.error('❌ Error getting event comments:', error);
    return [];
  }
};

// Cancel/Update event (host only)
export const updateEvent = async (
  eventId: string,
  updates: Partial<Omit<GroupEvent, 'id' | 'hostId' | 'createdAt'>>
): Promise<boolean> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('User not authenticated');

    // Verify user is the host
    const eventDoc = await getDoc(doc(groupEventsCollection, eventId));
    if (!eventDoc.exists()) throw new Error('Event not found');
    
    const eventData = eventDoc.data() as GroupEvent;
    if (eventData.hostId !== currentUser.uid) {
      throw new Error('Only host can update event');
    }

    await updateDoc(doc(groupEventsCollection, eventId), {
      ...updates,
      updatedAt: serverTimestamp()
    });

    console.log('✅ Event updated successfully');
    return true;
  } catch (error) {
    console.error('❌ Error updating event:', error);
    return false;
  }
};

// Get user's hosted events
export const getUserHostedEvents = async (): Promise<GroupEvent[]> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return [];

    const hostedEventsQuery = query(
      groupEventsCollection,
      where('hostId', '==', currentUser.uid),
      orderBy('dateTime', 'desc')
    );

    const querySnapshot = await getDocs(hostedEventsQuery);
    const events: GroupEvent[] = [];
    
    querySnapshot.forEach(doc => {
      events.push({ id: doc.id, ...doc.data() } as GroupEvent);
    });

    return events;
  } catch (error) {
    console.error('❌ Error getting hosted events:', error);
    return [];
  }
};

// Get user's attending events
export const getUserAttendingEvents = async (): Promise<GroupEvent[]> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return [];

    // Get user's RSVPs
    const rsvpQuery = query(
      eventAttendeesCollection,
      where('userId', '==', currentUser.uid),
      where('status', 'in', ['going', 'interested'])
    );

    const rsvpSnapshot = await getDocs(rsvpQuery);
    const eventIds = rsvpSnapshot.docs.map(doc => doc.data().eventId);

    if (eventIds.length === 0) return [];

    // Get the events (Note: Firestore 'in' queries are limited to 10 items)
    const events: GroupEvent[] = [];
    const batchSize = 10;
    
    for (let i = 0; i < eventIds.length; i += batchSize) {
      const batch = eventIds.slice(i, i + batchSize);
      const eventsQuery = query(
        groupEventsCollection,
        where(doc().id, 'in', batch),
        orderBy('dateTime', 'asc')
      );

      const eventsSnapshot = await getDocs(eventsQuery);
      eventsSnapshot.forEach(doc => {
        events.push({ id: doc.id, ...doc.data() } as GroupEvent);
      });
    }

    return events;
  } catch (error) {
    console.error('❌ Error getting attending events:', error);
    return [];
  }
};

// ================================
// COMPATIBILITY SCORING SYSTEM
// ================================

export interface CompatibilityScore {
  userId: string;
  score: number;
  breakdown: {
    interests: number;
    lifestyle: number;
    goals: number;
    age: number;
    location: number;
    prompts: number;
  };
  sharedInterests: string[];
  sharedGoals: string[];
  reasons: string[];
}

// Calculate compatibility score between two users
export const calculateCompatibilityScore = (
  currentUser: any,
  targetUser: any,
  distance?: number
): CompatibilityScore => {
  let totalScore = 0;
  const breakdown = {
    interests: 0,
    lifestyle: 0,
    goals: 0,
    age: 0,
    location: 0,
    prompts: 0
  };
  const sharedInterests: string[] = [];
  const sharedGoals: string[] = [];
  const reasons: string[] = [];

  // 1. Interests compatibility (30 points max)
  if (currentUser.interests && targetUser.interests) {
    const currentInterests = currentUser.interests.map((i: string) => i.toLowerCase());
    const targetInterests = targetUser.interests.map((i: string) => i.toLowerCase());
    
    const shared = currentInterests.filter((interest: string) => 
      targetInterests.includes(interest)
    );
    
    sharedInterests.push(...shared);
    const interestScore = Math.min((shared.length / Math.max(currentInterests.length, 1)) * 30, 30);
    breakdown.interests = interestScore;
    totalScore += interestScore;
    
    if (shared.length > 0) {
      reasons.push(`${shared.length} shared ${shared.length === 1 ? 'interest' : 'interests'}`);
    }
  }

  // 2. Lifestyle compatibility (20 points max)
  if (currentUser.datingData?.lifestyle && targetUser.datingData?.lifestyle) {
    let lifestyleScore = 0;
    const currentLifestyle = currentUser.datingData.lifestyle;
    const targetLifestyle = targetUser.datingData.lifestyle;
    
    // Exercise compatibility
    if (currentLifestyle.exercise && targetLifestyle.exercise) {
      const exerciseCompatibility = getLifestyleCompatibility(
        currentLifestyle.exercise, 
        targetLifestyle.exercise,
        ['never', 'rarely', 'sometimes', 'regularly', 'daily']
      );
      lifestyleScore += exerciseCompatibility * 10;
    }
    
    // Drinking compatibility  
    if (currentLifestyle.drinking && targetLifestyle.drinking) {
      const drinkingCompatibility = getLifestyleCompatibility(
        currentLifestyle.drinking,
        targetLifestyle.drinking,
        ['never', 'rarely', 'socially', 'regularly', 'frequently']
      );
      lifestyleScore += drinkingCompatibility * 10;
    }
    
    breakdown.lifestyle = lifestyleScore;
    totalScore += lifestyleScore;
    
    if (lifestyleScore > 15) {
      reasons.push('Compatible lifestyle choices');
    }
  }

  // 3. Relationship goals compatibility (25 points max)
  if (currentUser.datingData?.relationshipGoals && targetUser.datingData?.relationshipGoals) {
    const currentGoals = currentUser.datingData.relationshipGoals.map((g: string) => g.toLowerCase());
    const targetGoals = targetUser.datingData.relationshipGoals.map((g: string) => g.toLowerCase());
    
    const shared = currentGoals.filter((goal: string) => targetGoals.includes(goal));
    sharedGoals.push(...shared);
    
    const goalsScore = Math.min((shared.length / Math.max(currentGoals.length, 1)) * 25, 25);
    breakdown.goals = goalsScore;
    totalScore += goalsScore;
    
    if (shared.length > 0) {
      reasons.push(`Looking for the same things`);
    }
  }

  // 4. Age compatibility (10 points max)
  if (currentUser.age && targetUser.age) {
    const ageDiff = Math.abs(currentUser.age - targetUser.age);
    const ageScore = Math.max(10 - (ageDiff * 0.5), 0);
    breakdown.age = ageScore;
    totalScore += ageScore;
    
    if (ageScore > 7) {
      reasons.push('Similar age');
    }
  }

  // 5. Location compatibility (10 points max) 
  if (distance !== undefined) {
    const locationScore = Math.max(10 - (distance * 0.5), 0);
    breakdown.location = locationScore;
    totalScore += locationScore;
    
    if (distance < 5) {
      reasons.push('Lives nearby');
    }
  }

  // 6. Prompt compatibility (5 points max)
  if (currentUser.datingData?.prompts && targetUser.datingData?.prompts) {
    const promptScore = calculatePromptCompatibility(
      currentUser.datingData.prompts,
      targetUser.datingData.prompts
    );
    breakdown.prompts = promptScore;
    totalScore += promptScore;
    
    if (promptScore > 3) {
      reasons.push('Similar personality vibes');
    }
  }

  return {
    userId: targetUser.uid,
    score: Math.round(totalScore),
    breakdown,
    sharedInterests,
    sharedGoals,
    reasons: reasons.slice(0, 3) // Limit to top 3 reasons
  };
};

// Helper function for lifestyle compatibility
const getLifestyleCompatibility = (
  value1: string,
  value2: string,
  scale: string[]
): number => {
  const index1 = scale.indexOf(value1.toLowerCase());
  const index2 = scale.indexOf(value2.toLowerCase());
  
  if (index1 === -1 || index2 === -1) return 0;
  
  const difference = Math.abs(index1 - index2);
  return Math.max(1 - (difference * 0.3), 0);
};

// Helper function for prompt compatibility
const calculatePromptCompatibility = (prompts1: any[], prompts2: any[]): number => {
  let compatibility = 0;
  let comparisons = 0;
  
  for (const prompt1 of prompts1) {
    for (const prompt2 of prompts2) {
      if (prompt1.question === prompt2.question) {
        // Same question - compare answers using simple keyword matching
        const similarity = calculateTextSimilarity(prompt1.answer, prompt2.answer);
        compatibility += similarity;
        comparisons++;
      }
    }
  }
  
  return comparisons > 0 ? (compatibility / comparisons) * 5 : 0;
};

// Simple text similarity calculation
const calculateTextSimilarity = (text1: string, text2: string): number => {
  const words1 = text1.toLowerCase().split(/\W+/).filter(w => w.length > 2);
  const words2 = text2.toLowerCase().split(/\W+/).filter(w => w.length > 2);
  
  const commonWords = words1.filter(word => words2.includes(word));
  const totalWords = new Set([...words1, ...words2]).size;
  
  return totalWords > 0 ? commonWords.length / totalWords : 0;
};

// Get compatibility scores for multiple users
export const getCompatibilityScores = async (
  targetUserIds: string[],
  userLocation?: [number, number]
): Promise<CompatibilityScore[]> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return [];

    // Get current user's profile
    const currentUserProfile = await getUserById(currentUser.uid);
    if (!currentUserProfile) return [];

    // Get target users' profiles
    const scores: CompatibilityScore[] = [];
    
    for (const userId of targetUserIds) {
      const targetUser = await getUserById(userId);
      if (!targetUser) continue;

      // Calculate distance if location is provided
      let distance;
      if (userLocation && targetUser.location?.coordinates) {
        distance = calculateDistance(
          userLocation[0], userLocation[1],
          targetUser.location.coordinates.lat,
          targetUser.location.coordinates.lng
        );
      }

      const score = calculateCompatibilityScore(currentUserProfile, targetUser, distance);
      scores.push(score);
    }

    // Sort by score (highest first)
    return scores.sort((a, b) => b.score - a.score);
  } catch (error) {
    console.error('❌ Error calculating compatibility scores:', error);
    return [];
  }
};

// Generate conversation starters based on compatibility
export const generateConversationStarters = (
  currentUser: any,
  targetUser: any,
  compatibility: CompatibilityScore
): string[] => {
  const starters: string[] = [];

  // Based on shared interests
  if (compatibility.sharedInterests.length > 0) {
    const interest = compatibility.sharedInterests[0];
    starters.push(`I noticed we both love ${interest}! What got you into it?`);
    starters.push(`${interest} fan here too! Any recommendations?`);
  }

  // Based on shared relationship goals
  if (compatibility.sharedGoals.length > 0) {
    starters.push(`I like that we're looking for similar things. What's your ideal first date?`);
  }

  // Based on prompts with same questions
  if (currentUser.datingData?.prompts && targetUser.datingData?.prompts) {
    for (const currentPrompt of currentUser.datingData.prompts) {
      for (const targetPrompt of targetUser.datingData.prompts) {
        if (currentPrompt.question === targetPrompt.question) {
          starters.push(`Loved your answer to "${currentPrompt.question}" - ${generatePromptStarter(targetPrompt.answer)}`);
          break;
        }
      }
    }
  }

  // Based on location
  if (compatibility.breakdown.location > 7) {
    starters.push(`We're neighbors! Know any good spots around here?`);
    starters.push(`Local here too! What's your favorite thing about the area?`);
  }

  // General high compatibility
  if (compatibility.score > 70) {
    starters.push(`We seem really compatible! What's been the highlight of your week?`);
  }

  // Fallback starters
  if (starters.length === 0) {
    starters.push(`Hey! Your profile caught my attention. How's your day going?`);
    starters.push(`Hi there! What's keeping you busy these days?`);
  }

  return starters.slice(0, 3); // Return top 3 starters
};

// Helper for prompt-based starters
const generatePromptStarter = (answer: string): string => {
  const responses = [
    `tell me more!`,
    `that sounds amazing!`,
    `I can relate to that`,
    `that's so interesting`,
    `I love that perspective`
  ];
  return responses[Math.floor(Math.random() * responses.length)];
};

// ================================
// RELATIONSHIP INTENTION FILTERS
// ================================

export interface IntentionFilters {
  relationshipGoals: string[];
  lookingFor: string[];
  timeframe: 'now' | 'soon' | 'someday' | 'exploring';
  commitment: 'casual' | 'serious' | 'both' | 'undecided';
}

// Get users filtered by relationship intentions
export const getUsersByIntentions = async (
  filters: Partial<IntentionFilters>,
  location?: [number, number],
  radiusKm: number = 25
): Promise<any[]> => {
  try {
    const usersCollection = collection(db, 'users');
    let userQuery = query(usersCollection, limit(100));

    // Apply basic filters
    const querySnapshot = await getDocs(userQuery);
    let users: any[] = [];
    
    querySnapshot.forEach(doc => {
      const userData = { uid: doc.id, ...doc.data() };
      users.push(userData);
    });

    // Filter by relationship goals
    if (filters.relationshipGoals && filters.relationshipGoals.length > 0) {
      users = users.filter(user => {
        if (!user.datingData?.relationshipGoals) return false;
        return user.datingData.relationshipGoals.some((goal: string) =>
          filters.relationshipGoals!.some(filterGoal => 
            goal.toLowerCase().includes(filterGoal.toLowerCase())
          )
        );
      });
    }

    // Filter by what they're looking for
    if (filters.lookingFor && filters.lookingFor.length > 0) {
      users = users.filter(user => {
        if (!user.nowData?.lookingFor && !user.datingData?.interests) return false;
        
        const userLookingFor = [
          ...(user.nowData?.lookingFor || []),
          ...(user.datingData?.interests || [])
        ].map(item => item.toLowerCase());
        
        return filters.lookingFor!.some(filter =>
          userLookingFor.some(item => item.includes(filter.toLowerCase()))
        );
      });
    }

    // Filter by location if provided
    if (location) {
      users = users.filter(user => {
        if (!user.location?.coordinates) return false;
        
        const distance = calculateDistance(
          location[0], location[1],
          user.location.coordinates.lat,
          user.location.coordinates.lng
        );
        
        return distance <= radiusKm;
      });
    }

    return users;
  } catch (error) {
    console.error('❌ Error filtering users by intentions:', error);
    return [];
  }
};

// Get popular relationship goals for filters
export const getPopularRelationshipGoals = async (): Promise<string[]> => {
  try {
    const usersCollection = collection(db, 'users');
    const querySnapshot = await getDocs(usersCollection);
    
    const goalCounts: Record<string, number> = {};
    
    querySnapshot.forEach(doc => {
      const userData = doc.data();
      if (userData.datingData?.relationshipGoals) {
        userData.datingData.relationshipGoals.forEach((goal: string) => {
          goalCounts[goal] = (goalCounts[goal] || 0) + 1;
        });
      }
    });

    // Return top 10 most popular goals
    return Object.entries(goalCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 10)
      .map(([goal]) => goal);
  } catch (error) {
    console.error('❌ Error getting popular relationship goals:', error);
    return [
      'Long-term relationship',
      'Something casual',
      'New friends', 
      'Figuring it out',
      'Marriage',
      'Life partner',
      'Fun & connections',
      'Open to anything',
      'Dating with intention',
      'Companionship'
    ];
  }
};

// Enhanced compatibility scoring that includes intention matching
export const calculateIntentionCompatibility = (
  currentUser: any,
  targetUser: any
): number => {
  let score = 0;
  
  // Match relationship goals (40 points max)
  if (currentUser.datingData?.relationshipGoals && targetUser.datingData?.relationshipGoals) {
    const currentGoals = currentUser.datingData.relationshipGoals.map((g: string) => g.toLowerCase());
    const targetGoals = targetUser.datingData.relationshipGoals.map((g: string) => g.toLowerCase());
    
    // Exact matches
    const exactMatches = currentGoals.filter((goal: string) => targetGoals.includes(goal));
    score += exactMatches.length * 10;
    
    // Compatible matches (e.g., "serious relationship" + "marriage")
    const compatiblePairs: Record<string, string[]> = {
      'long-term relationship': ['marriage', 'life partner', 'serious relationship'],
      'something casual': ['fun & connections', 'figuring it out', 'open to anything'],
      'new friends': ['companionship', 'fun & connections'],
      'marriage': ['long-term relationship', 'life partner', 'serious relationship']
    };
    
    currentGoals.forEach((goal: string) => {
      if (compatiblePairs[goal]) {
        const compatible = targetGoals.some((target: string) =>
          compatiblePairs[goal].some(comp => target.includes(comp))
        );
        if (compatible) score += 5;
      }
    });
  }
  
  // Match looking for (Now mode) (20 points max)
  if (currentUser.nowData?.lookingFor && targetUser.nowData?.lookingFor) {
    const currentLooking = currentUser.nowData.lookingFor.map((l: string) => l.toLowerCase());
    const targetLooking = targetUser.nowData.lookingFor.map((l: string) => l.toLowerCase());
    
    const matches = currentLooking.filter((item: string) => targetLooking.includes(item));
    score += Math.min(matches.length * 5, 20);
  }
  
  return Math.min(score, 60); // Max 60 points for intention compatibility
};

// ================================
// VOICE/VIDEO PROMPTS SYSTEM  
// ================================

export interface MediaPrompt {
  id: string;
  question: string;
  type: 'voice' | 'video';
  mediaUrl?: string;
  thumbnailUrl?: string;
  duration?: number;
  transcription?: string;
  isProcessing?: boolean;
  createdAt?: any;
  approved?: boolean;
}

export interface MediaPromptResponse {
  id: string;
  promptId: string;
  userId: string;
  mediaUrl: string;
  thumbnailUrl?: string;
  duration: number;
  transcription?: string;
  isProcessing: boolean;
  createdAt: any;
  approved: boolean;
}

// Popular voice/video prompt questions
export const MEDIA_PROMPT_QUESTIONS = [
  {
    id: 'intro',
    question: "Tell us about yourself in 30 seconds",
    type: 'video' as const,
    category: 'introduction'
  },
  {
    id: 'laugh',
    question: "What always makes you laugh?",
    type: 'voice' as const,
    category: 'personality'
  },
  {
    id: 'passion',
    question: "What are you most passionate about?",
    type: 'voice' as const,
    category: 'interests'
  },
  {
    id: 'perfect_day',
    question: "Describe your perfect day",
    type: 'voice' as const,
    category: 'lifestyle'
  },
  {
    id: 'travel_story',
    question: "Tell us about your best travel experience",
    type: 'video' as const,
    category: 'experiences'
  },
  {
    id: 'hidden_talent',
    question: "What's your hidden talent?",
    type: 'video' as const,
    category: 'fun'
  },
  {
    id: 'motivation',
    question: "What motivates you every day?",
    type: 'voice' as const,
    category: 'values'
  },
  {
    id: 'ideal_partner',
    question: "What are you looking for in a partner?",
    type: 'voice' as const,
    category: 'dating'
  }
];

// Upload media prompt response
export const uploadMediaPromptResponse = async (
  promptId: string,
  mediaFile: File,
  type: 'voice' | 'video'
): Promise<string | null> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return null;

    // Create storage reference
    const fileExtension = mediaFile.name.split('.').pop();
    const fileName = `${currentUser.uid}_${promptId}_${Date.now()}.${fileExtension}`;
    const mediaRef = ref(storage, `media-prompts/${type}/${fileName}`);

    // Upload file
    const uploadResult = await uploadBytes(mediaRef, mediaFile);
    const mediaUrl = await getDownloadURL(uploadResult.ref);

    // Create response record
    const mediaResponseData: Omit<MediaPromptResponse, 'id'> = {
      promptId,
      userId: currentUser.uid,
      mediaUrl,
      duration: type === 'voice' ? 30 : 60, // Default durations
      isProcessing: true,
      createdAt: serverTimestamp(),
      approved: false
    };

    const mediaResponsesCollection = collection(db, 'mediaPromptResponses');
    const docRef = await addDoc(mediaResponsesCollection, mediaResponseData);

    // Update user's profile with the new media prompt
    const userRef = doc(db, 'users', currentUser.uid);
    await updateDoc(userRef, {
      [`mediaPrompts.${promptId}`]: {
        responseId: docRef.id,
        mediaUrl,
        type,
        isProcessing: true,
        createdAt: serverTimestamp()
      }
    });

    return docRef.id;
  } catch (error) {
    console.error('❌ Error uploading media prompt:', error);
    return null;
  }
};

// Get user's media prompt responses
export const getUserMediaPrompts = async (userId: string): Promise<MediaPromptResponse[]> => {
  try {
    const mediaResponsesCollection = collection(db, 'mediaPromptResponses');
    const userMediaQuery = query(
      mediaResponsesCollection,
      where('userId', '==', userId),
      where('approved', '==', true),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(userMediaQuery);
    const responses: MediaPromptResponse[] = [];

    querySnapshot.forEach(doc => {
      responses.push({ id: doc.id, ...doc.data() } as MediaPromptResponse);
    });

    return responses;
  } catch (error) {
    console.error('❌ Error getting user media prompts:', error);
    return [];
  }
};

// Delete media prompt response
export const deleteMediaPromptResponse = async (responseId: string): Promise<boolean> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return false;

    // Get the response to find the media URL
    const responseRef = doc(db, 'mediaPromptResponses', responseId);
    const responseSnap = await getDoc(responseRef);
    
    if (!responseSnap.exists()) return false;
    
    const responseData = responseSnap.data() as MediaPromptResponse;
    
    // Only allow user to delete their own responses
    if (responseData.userId !== currentUser.uid) return false;

    // Delete media file from storage
    if (responseData.mediaUrl) {
      try {
        const mediaRef = ref(storage, responseData.mediaUrl);
        await deleteObject(mediaRef);
      } catch (storageError) {
        console.warn('Could not delete media file:', storageError);
      }
    }

    // Delete thumbnail if exists
    if (responseData.thumbnailUrl) {
      try {
        const thumbnailRef = ref(storage, responseData.thumbnailUrl);
        await deleteObject(thumbnailRef);
      } catch (storageError) {
        console.warn('Could not delete thumbnail:', storageError);
      }
    }

    // Delete response document
    await deleteDoc(responseRef);

    // Remove from user profile
    const userRef = doc(db, 'users', currentUser.uid);
    await updateDoc(userRef, {
      [`mediaPrompts.${responseData.promptId}`]: null
    });

    return true;
  } catch (error) {
    console.error('❌ Error deleting media prompt response:', error);
    return false;
  }
};

// ================================
// AI-POWERED PROMPT FEEDBACK SYSTEM
// ================================

export interface PromptFeedback {
  score: number; // 0-100
  suggestions: string[];
  strengths: string[];
  category: 'excellent' | 'good' | 'needs_work';
  wordCount: number;
  tone: 'positive' | 'neutral' | 'negative';
  engagement: 'high' | 'medium' | 'low';
}

// Analyze prompt quality and provide feedback
export const analyzePromptQuality = (answer: string, question: string): PromptFeedback => {
  const wordCount = answer.trim().split(/\s+/).length;
  let score = 50; // Base score
  const suggestions: string[] = [];
  const strengths: string[] = [];

  // Word count analysis
  if (wordCount < 5) {
    score -= 20;
    suggestions.push("Try to be more descriptive - aim for at least 10-20 words");
  } else if (wordCount > 100) {
    score -= 10;
    suggestions.push("Consider keeping it more concise - shorter responses often work better");
  } else if (wordCount >= 15 && wordCount <= 60) {
    score += 15;
    strengths.push("Great length - detailed but not too long");
  }

  // Specificity check
  const specificWords = [
    'hiking', 'cooking', 'photography', 'travel', 'music', 'art', 'coffee', 'wine',
    'books', 'movies', 'beach', 'mountains', 'yoga', 'running', 'dancing', 'skiing'
  ];
  const hasSpecifics = specificWords.some(word => 
    answer.toLowerCase().includes(word.toLowerCase())
  );
  
  if (hasSpecifics) {
    score += 10;
    strengths.push("Specific interests make you more memorable");
  } else {
    suggestions.push("Include specific hobbies or interests to stand out");
  }

  // Positivity check
  const positiveWords = [
    'love', 'enjoy', 'passionate', 'excited', 'amazing', 'great', 'wonderful',
    'fun', 'adventure', 'explore', 'discover', 'favorite', 'happy'
  ];
  const negativeWords = [
    'hate', 'don\'t', 'never', 'boring', 'tired', 'stressed', 'annoying'
  ];

  const positiveCount = positiveWords.filter(word =>
    answer.toLowerCase().includes(word.toLowerCase())
  ).length;
  const negativeCount = negativeWords.filter(word =>
    answer.toLowerCase().includes(word.toLowerCase())
  ).length;

  let tone: 'positive' | 'neutral' | 'negative' = 'neutral';
  
  if (positiveCount > negativeCount) {
    score += 10;
    strengths.push("Positive tone attracts more connections");
    tone = 'positive';
  } else if (negativeCount > positiveCount) {
    score -= 15;
    suggestions.push("Try focusing on what you enjoy rather than what you don't");
    tone = 'negative';
  }

  // Question-specific analysis
  const lowerQuestion = question.toLowerCase();
  const lowerAnswer = answer.toLowerCase();

  if (lowerQuestion.includes('perfect') && lowerQuestion.includes('date')) {
    if (lowerAnswer.includes('coffee') || lowerAnswer.includes('walk') || lowerAnswer.includes('museum')) {
      score += 5;
      strengths.push("Casual first date ideas are approachable");
    }
  }

  if (lowerQuestion.includes('passionate') || lowerQuestion.includes('love')) {
    if (wordCount >= 20) {
      score += 10;
      strengths.push("Detailed passion shows authenticity");
    }
  }

  // Engagement potential
  let engagement: 'high' | 'medium' | 'low' = 'medium';
  const conversationStarters = [
    '?', 'you', 'what', 'how', 'where', 'love to', 'would love', 'tell me',
    'ask me', 'favorite', 'recommend'
  ];
  
  const hasConversationHooks = conversationStarters.some(hook =>
    lowerAnswer.includes(hook.toLowerCase())
  );

  if (hasConversationHooks) {
    score += 10;
    strengths.push("Creates natural conversation opportunities");
    engagement = 'high';
  } else if (wordCount < 10) {
    engagement = 'low';
    suggestions.push("Add conversation hooks to encourage messages");
  }

  // Clichés to avoid
  const cliches = [
    'work hard play hard', 'live laugh love', 'love to travel',
    'foodie', 'netflix', 'the office', 'friends'
  ];
  
  const hasCliches = cliches.some(cliche =>
    lowerAnswer.includes(cliche.toLowerCase())
  );

  if (hasCliches) {
    score -= 5;
    suggestions.push("Try to avoid common phrases - be more unique");
  }

  // Determine category
  let category: 'excellent' | 'good' | 'needs_work';
  if (score >= 80) {
    category = 'excellent';
  } else if (score >= 60) {
    category = 'good';
  } else {
    category = 'needs_work';
  }

  // Ensure score is within bounds
  score = Math.max(0, Math.min(100, score));

  return {
    score,
    suggestions: suggestions.slice(0, 3), // Top 3 suggestions
    strengths: strengths.slice(0, 3), // Top 3 strengths
    category,
    wordCount,
    tone,
    engagement
  };
};

// Get improvement suggestions for entire profile
export const getProfileImprovementSuggestions = async (userId: string): Promise<{
  overallScore: number;
  suggestions: string[];
  strengths: string[];
  promptAnalysis: { question: string; feedback: PromptFeedback }[];
}> => {
  try {
    const user = await getUserById(userId);
    if (!user) throw new Error('User not found');

    let totalScore = 0;
    let promptCount = 0;
    const promptAnalysis: { question: string; feedback: PromptFeedback }[] = [];
    const allSuggestions: string[] = [];
    const allStrengths: string[] = [];

    // Analyze dating prompts
    if (user.datingData?.prompts) {
      for (const prompt of user.datingData.prompts) {
        const feedback = analyzePromptQuality(prompt.answer, prompt.question);
        promptAnalysis.push({ question: prompt.question, feedback });
        totalScore += feedback.score;
        promptCount++;
        
        allSuggestions.push(...feedback.suggestions);
        allStrengths.push(...feedback.strengths);
      }
    }

    // Analyze bio
    if (user.bio || user.datingData?.bio) {
      const bio = user.bio || user.datingData?.bio || '';
      const bioFeedback = analyzePromptQuality(bio, "Tell us about yourself");
      promptAnalysis.push({ question: "Your bio", feedback: bioFeedback });
      totalScore += bioFeedback.score;
      promptCount++;
      
      allSuggestions.push(...bioFeedback.suggestions);
      allStrengths.push(...bioFeedback.strengths);
    }

    const overallScore = promptCount > 0 ? Math.round(totalScore / promptCount) : 0;

    // Remove duplicates and get top suggestions
    const uniqueSuggestions = [...new Set(allSuggestions)].slice(0, 5);
    const uniqueStrengths = [...new Set(allStrengths)].slice(0, 5);

    return {
      overallScore,
      suggestions: uniqueSuggestions,
      strengths: uniqueStrengths,
      promptAnalysis
    };
  } catch (error) {
    console.error('❌ Error analyzing profile:', error);
    return {
      overallScore: 0,
      suggestions: [],
      strengths: [],
      promptAnalysis: []
    };
  }
};

// Generate prompt suggestions based on interests and goals
export const generatePromptSuggestions = (
  interests: string[] = [],
  relationshipGoals: string[] = []
): { question: string; sampleAnswers: string[] }[] => {
  const suggestions = [];

  // Interest-based prompts
  if (interests.includes('Travel')) {
    suggestions.push({
      question: "My most memorable travel experience was...",
      sampleAnswers: [
        "Getting lost in Tokyo and discovering the best ramen spot by accident",
        "Watching sunrise over Machu Picchu with complete strangers who became lifelong friends"
      ]
    });
  }

  if (interests.includes('Food') || interests.includes('Cooking')) {
    suggestions.push({
      question: "The way to my heart is...",
      sampleAnswers: [
        "Through my stomach - cook me pasta and I'm yours",
        "A perfectly made cappuccino and good conversation"
      ]
    });
  }

  if (interests.includes('Music')) {
    suggestions.push({
      question: "My go-to karaoke song is...",
      sampleAnswers: [
        "Don't Stop Believin' - I may not hit the high notes but I'll give it my all",
        "I'm more of a backup dancer, but I'll cheer you on!"
      ]
    });
  }

  // Goal-based prompts
  const isSerious = relationshipGoals.some(goal => 
    goal.toLowerCase().includes('long-term') || 
    goal.toLowerCase().includes('marriage') ||
    goal.toLowerCase().includes('serious')
  );

  if (isSerious) {
    suggestions.push({
      question: "I know I've found the right person when...",
      sampleAnswers: [
        "We can sit in comfortable silence and still feel connected",
        "They make ordinary moments feel extraordinary"
      ]
    });
  }

  // Universal prompts
  suggestions.push(
    {
      question: "I'm overly competitive about...",
      sampleAnswers: [
        "Board games - I will destroy you at Monopoly",
        "Finding the best coffee shop in any new city"
      ]
    },
    {
      question: "My simple pleasure is...",
      sampleAnswers: [
        "Sunday morning farmers markets and fresh flowers",
        "The first sip of coffee while watching sunrise"
      ]
    }
  );

  return suggestions.slice(0, 4);
};

// ================================
// ADMIN FEATURE MANAGEMENT SYSTEM
// ================================

export interface FeatureConfig {
  id: string;
  name: string;
  description: string;
  isEnabled: boolean;
  isPremium: boolean;
  category: 'core' | 'premium' | 'experimental' | 'location' | 'ai';
  userLimit?: number; // Daily/monthly limits
  limitUnit?: 'per_day' | 'per_month' | 'total';
  rolloutPercentage?: number; // For gradual rollouts (0-100)
  minAppVersion?: string;
  dependencies?: string[]; // Other feature IDs this depends on
  analytics?: {
    enabled: boolean;
    events: string[];
  };
  createdAt?: any;
  updatedAt?: any;
  updatedBy?: string;
}

export interface UserTierLimits {
  free: {
    dailyLikes: number;
    monthlyMatches: number;
    dailyMessages: number;
    cruisingSpots: number;
    events: number;
    mediaPrompts: number;
    aiFeatures: boolean;
  };
  premium: {
    dailyLikes: number;
    monthlyMatches: number;
    dailyMessages: number;
    cruisingSpots: number;
    events: number;
    mediaPrompts: number;
    aiFeatures: boolean;
    seeWhoLikes: boolean;
    unlimitedRewind: boolean;
    incognitoMode: boolean;
  };
}

// Default feature configurations
export const DEFAULT_FEATURES: FeatureConfig[] = [
  {
    id: 'basic_messaging',
    name: 'Basic Messaging',
    description: 'Core messaging functionality',
    isEnabled: true,
    isPremium: false,
    category: 'core',
    userLimit: 50,
    limitUnit: 'per_day'
  },
  {
    id: 'unlimited_likes',
    name: 'Unlimited Likes',
    description: 'No daily limit on likes',
    isEnabled: true,
    isPremium: true,
    category: 'premium'
  },
  {
    id: 'see_who_likes',
    name: 'See Who Likes You',
    description: 'View users who liked your profile',
    isEnabled: true,
    isPremium: true,
    category: 'premium'
  },
  {
    id: 'compatibility_scores',
    name: 'Compatibility Scores',
    description: 'AI-powered compatibility matching',
    isEnabled: true,
    isPremium: false,
    category: 'ai',
    rolloutPercentage: 100
  },
  {
    id: 'voice_prompts',
    name: 'Voice Prompts',
    description: 'Audio responses to prompts',
    isEnabled: true,
    isPremium: false,
    category: 'experimental',
    userLimit: 3,
    limitUnit: 'total'
  },
  {
    id: 'video_prompts',
    name: 'Video Prompts',
    description: 'Video responses to prompts',
    isEnabled: true,
    isPremium: true,
    category: 'experimental',
    userLimit: 1,
    limitUnit: 'total',
    dependencies: ['voice_prompts']
  },
  {
    id: 'cruising_map',
    name: 'Cruising Map',
    description: 'Location-based spot discovery',
    isEnabled: true,
    isPremium: false,
    category: 'location',
    rolloutPercentage: 100
  },
  {
    id: 'group_events',
    name: 'Group Events',
    description: 'Create and join local events',
    isEnabled: true,
    isPremium: false,
    category: 'location'
  }
];

// Get app feature configuration
export const getFeatureConfig = async (): Promise<FeatureConfig[]> => {
  try {
    const configRef = doc(db, 'appSettings', 'features');
    const configSnap = await getDoc(configRef);
    
    if (configSnap.exists()) {
      const data = configSnap.data();
      return data.features || DEFAULT_FEATURES;
    }
    
    return DEFAULT_FEATURES;
  } catch (error) {
    console.error('❌ Error getting feature config:', error);
    return DEFAULT_FEATURES;
  }
};

// Update feature configuration (admin only)
export const updateFeatureConfig = async (
  featureId: string,
  updates: Partial<FeatureConfig>
): Promise<boolean> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return false;

    // Check admin permissions
    const user = await getUserById(currentUser.uid);
    if (user?.role !== 'admin') return false;

    const configRef = doc(db, 'appSettings', 'features');
    const configSnap = await getDoc(configRef);
    
    let features: FeatureConfig[] = DEFAULT_FEATURES;
    
    if (configSnap.exists()) {
      features = configSnap.data().features || DEFAULT_FEATURES;
    }

    // Find and update the feature
    const featureIndex = features.findIndex(f => f.id === featureId);
    if (featureIndex === -1) {
      // Create new feature
      const newFeature: FeatureConfig = {
        id: featureId,
        name: updates.name || featureId,
        description: updates.description || '',
        isEnabled: updates.isEnabled ?? true,
        isPremium: updates.isPremium ?? false,
        category: updates.category || 'core',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid,
        ...updates
      };
      features.push(newFeature);
    } else {
      // Update existing feature
      features[featureIndex] = {
        ...features[featureIndex],
        ...updates,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid
      };
    }

    // Save to database
    await setDoc(configRef, { features }, { merge: true });
    return true;
  } catch (error) {
    console.error('❌ Error updating feature config:', error);
    return false;
  }
};

// Check if user has access to a feature
export const hasFeatureAccess = (
  feature: FeatureConfig,
  userTier: 'free' | 'premium' | 'admin',
  userId?: string
): boolean => {
  // Admin has access to everything
  if (userTier === 'admin') return true;
  
  // Feature must be enabled
  if (!feature.isEnabled) return false;
  
  // Check premium requirement
  if (feature.isPremium && userTier === 'free') return false;
  
  // Check rollout percentage
  if (feature.rolloutPercentage !== undefined) {
    if (userId) {
      // Use user ID to create consistent rollout decision
      const hash = userId.split('').reduce((a, b) => {
        a = ((a << 5) - a) + b.charCodeAt(0);
        return a & a;
      }, 0);
      const userPercentage = Math.abs(hash) % 100;
      if (userPercentage >= feature.rolloutPercentage) return false;
    } else {
      // Random rollout if no user ID
      if (Math.random() * 100 >= feature.rolloutPercentage) return false;
    }
  }
  
  return true;
};

// Get user's feature access
export const getUserFeatureAccess = async (userId: string): Promise<Record<string, boolean>> => {
  try {
    const user = await getUserById(userId);
    if (!user) return {};

    const features = await getFeatureConfig();
    const access: Record<string, boolean> = {};

    for (const feature of features) {
      access[feature.id] = hasFeatureAccess(feature, user.role || 'free', userId);
    }

    return access;
  } catch (error) {
    console.error('❌ Error getting user feature access:', error);
    return {};
  }
};

// Track feature usage
export const trackFeatureUsage = async (
  featureId: string,
  action: string,
  metadata?: Record<string, any>
): Promise<void> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    const usageData = {
      userId: currentUser.uid,
      featureId,
      action,
      metadata: metadata || {},
      timestamp: serverTimestamp(),
      userAgent: navigator.userAgent,
      platform: 'web'
    };

    const usageCollection = collection(db, 'featureUsage');
    await addDoc(usageCollection, usageData);
  } catch (error) {
    console.error('❌ Error tracking feature usage:', error);
  }
};

// Get feature usage analytics (admin only)
export const getFeatureAnalytics = async (
  featureId?: string,
  dateRange?: { start: Date; end: Date }
): Promise<{
  totalUsage: number;
  uniqueUsers: number;
  usageByDate: { date: string; count: number }[];
  topActions: { action: string; count: number }[];
}> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('Not authenticated');

    const user = await getUserById(currentUser.uid);
    if (user?.role !== 'admin') throw new Error('Admin access required');

    const usageCollection = collection(db, 'featureUsage');
    let usageQuery = query(usageCollection, orderBy('timestamp', 'desc'));

    if (featureId) {
      usageQuery = query(usageQuery, where('featureId', '==', featureId));
    }

    const querySnapshot = await getDocs(usageQuery);
    const usageData: any[] = [];
    
    querySnapshot.forEach(doc => {
      const data = doc.data();
      usageData.push(data);
    });

    // Filter by date range if provided
    let filteredData = usageData;
    if (dateRange) {
      filteredData = usageData.filter(item => {
        const timestamp = item.timestamp?.toDate();
        return timestamp && timestamp >= dateRange.start && timestamp <= dateRange.end;
      });
    }

    // Calculate analytics
    const uniqueUsers = new Set(filteredData.map(item => item.userId)).size;
    const totalUsage = filteredData.length;

    // Usage by date
    const dateGroups: Record<string, number> = {};
    filteredData.forEach(item => {
      const date = item.timestamp?.toDate()?.toISOString().split('T')[0];
      if (date) {
        dateGroups[date] = (dateGroups[date] || 0) + 1;
      }
    });

    const usageByDate = Object.entries(dateGroups).map(([date, count]) => ({
      date,
      count
    }));

    // Top actions
    const actionGroups: Record<string, number> = {};
    filteredData.forEach(item => {
      actionGroups[item.action] = (actionGroups[item.action] || 0) + 1;
    });

    const topActions = Object.entries(actionGroups)
      .map(([action, count]) => ({ action, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    return {
      totalUsage,
      uniqueUsers,
      usageByDate,
      topActions
    };
  } catch (error) {
    console.error('❌ Error getting feature analytics:', error);
    return {
      totalUsage: 0,
      uniqueUsers: 0,
      usageByDate: [],
      topActions: []
    };
  }
};

// ================================
// PAID/FREE TIER RESTRICTIONS
// ================================

export const DEFAULT_TIER_LIMITS: UserTierLimits = {
  free: {
    dailyLikes: 10,
    monthlyMatches: 50,
    dailyMessages: 25,
    cruisingSpots: 3, // Can create 3 spots
    events: 2, // Can create 2 events per month
    mediaPrompts: 1, // 1 voice prompt
    aiFeatures: false
  },
  premium: {
    dailyLikes: -1, // Unlimited
    monthlyMatches: -1, // Unlimited
    dailyMessages: -1, // Unlimited
    cruisingSpots: 20,
    events: 10,
    mediaPrompts: 5, // 3 voice + 2 video
    aiFeatures: true,
    seeWhoLikes: true,
    unlimitedRewind: true,
    incognitoMode: true
  }
};

// Track daily usage for limits
export const trackDailyUsage = async (
  action: 'like' | 'message' | 'match' | 'spot' | 'event' | 'media_prompt',
  amount: number = 1
): Promise<boolean> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return false;

    const today = new Date().toISOString().split('T')[0];
    const usageRef = doc(db, 'userUsage', `${currentUser.uid}_${today}`);
    
    const usageSnap = await getDoc(usageRef);
    let currentUsage = usageSnap.exists() ? usageSnap.data() : {};

    // Initialize usage if doesn't exist
    if (!currentUsage[action]) {
      currentUsage[action] = 0;
    }

    // Check limits
    const user = await getUserById(currentUser.uid);
    const userTier = user?.role === 'premium' ? 'premium' : 'free';
    const limits = DEFAULT_TIER_LIMITS[userTier];

    let dailyLimit;
    switch (action) {
      case 'like':
        dailyLimit = limits.dailyLikes;
        break;
      case 'message':
        dailyLimit = limits.dailyMessages;
        break;
      case 'spot':
        dailyLimit = limits.cruisingSpots;
        break;
      case 'event':
        dailyLimit = limits.events;
        break;
      case 'media_prompt':
        dailyLimit = limits.mediaPrompts;
        break;
      default:
        dailyLimit = -1; // No limit
    }

    // Check if user would exceed limit
    if (dailyLimit !== -1 && currentUsage[action] + amount > dailyLimit) {
      return false; // Limit exceeded
    }

    // Update usage
    currentUsage[action] += amount;
    currentUsage.lastUpdated = serverTimestamp();

    await setDoc(usageRef, currentUsage, { merge: true });
    return true;
  } catch (error) {
    console.error('❌ Error tracking daily usage:', error);
    return false;
  }
};

// Get user's current usage
export const getUserDailyUsage = async (userId: string): Promise<Record<string, number>> => {
  try {
    const today = new Date().toISOString().split('T')[0];
    const usageRef = doc(db, 'userUsage', `${userId}_${today}`);
    const usageSnap = await getDoc(usageRef);
    
    return usageSnap.exists() ? usageSnap.data() : {};
  } catch (error) {
    console.error('❌ Error getting user usage:', error);
    return {};
  }
};

// Check if user can perform action
export const canPerformAction = async (
  action: 'like' | 'message' | 'match' | 'spot' | 'event' | 'media_prompt',
  amount: number = 1
): Promise<{
  allowed: boolean;
  remaining?: number;
  upgradeRequired?: boolean;
  resetTime?: Date;
}> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      return { allowed: false, upgradeRequired: false };
    }

    const user = await getUserById(currentUser.uid);
    if (!user) {
      return { allowed: false, upgradeRequired: false };
    }

    // Admin can do everything
    if (user.role === 'admin') {
      return { allowed: true };
    }

    const userTier = user.role === 'premium' ? 'premium' : 'free';
    const limits = DEFAULT_TIER_LIMITS[userTier];
    const currentUsage = await getUserDailyUsage(currentUser.uid);

    let dailyLimit;
    let usageKey;
    
    switch (action) {
      case 'like':
        dailyLimit = limits.dailyLikes;
        usageKey = 'like';
        break;
      case 'message':
        dailyLimit = limits.dailyMessages;
        usageKey = 'message';
        break;
      case 'spot':
        dailyLimit = limits.cruisingSpots;
        usageKey = 'spot';
        break;
      case 'event':
        dailyLimit = limits.events;
        usageKey = 'event';
        break;
      case 'media_prompt':
        dailyLimit = limits.mediaPrompts;
        usageKey = 'media_prompt';
        break;
      default:
        return { allowed: true };
    }

    // Unlimited for premium users
    if (dailyLimit === -1) {
      return { allowed: true };
    }

    const used = currentUsage[usageKey] || 0;
    const remaining = dailyLimit - used;

    if (remaining < amount) {
      // Calculate reset time (midnight tomorrow)
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);

      return {
        allowed: false,
        remaining: 0,
        upgradeRequired: userTier === 'free',
        resetTime: tomorrow
      };
    }

    return {
      allowed: true,
      remaining: remaining - amount
    };
  } catch (error) {
    console.error('❌ Error checking action permission:', error);
    return { allowed: false, upgradeRequired: false };
  }
};

// Subscription management
export interface UserSubscription {
  userId: string;
  tier: 'free' | 'premium';
  status: 'active' | 'cancelled' | 'expired' | 'trial';
  startDate: any;
  endDate?: any;
  autoRenew: boolean;
  paymentMethod?: string;
  priceId?: string;
  customerId?: string;
  trialEndsAt?: any;
}

// Update user subscription
export const updateUserSubscription = async (
  userId: string,
  subscriptionData: Partial<UserSubscription>
): Promise<boolean> => {
  try {
    const subscriptionRef = doc(db, 'subscriptions', userId);
    await setDoc(subscriptionRef, {
      userId,
      ...subscriptionData,
      updatedAt: serverTimestamp()
    }, { merge: true });

    // Update user role
    if (subscriptionData.tier) {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        role: subscriptionData.tier,
        lastSubscriptionUpdate: serverTimestamp()
      });
    }

    return true;
  } catch (error) {
    console.error('❌ Error updating subscription:', error);
    return false;
  }
};

// Get user subscription
export const getUserSubscription = async (userId: string): Promise<UserSubscription | null> => {
  try {
    const subscriptionRef = doc(db, 'subscriptions', userId);
    const subscriptionSnap = await getDoc(subscriptionRef);
    
    if (subscriptionSnap.exists()) {
      return subscriptionSnap.data() as UserSubscription;
    }
    
    // Return default free subscription
    return {
      userId,
      tier: 'free',
      status: 'active',
      startDate: serverTimestamp(),
      autoRenew: false
    };
  } catch (error) {
    console.error('❌ Error getting subscription:', error);
    return null;
  }
};

// Check subscription validity
export const isSubscriptionValid = (subscription: UserSubscription): boolean => {
  if (subscription.tier === 'free') return true;
  
  if (subscription.status === 'expired' || subscription.status === 'cancelled') {
    return false;
  }
  
  if (subscription.endDate) {
    const endDate = subscription.endDate.toDate();
    return endDate > new Date();
  }
  
  return subscription.status === 'active';
};

// Get usage analytics for billing
export const getUsageAnalytics = async (
  userId: string,
  days: number = 30
): Promise<{
  dailyUsage: { date: string; likes: number; messages: number; spots: number; events: number }[];
  totalUsage: { likes: number; messages: number; spots: number; events: number };
  averageDaily: { likes: number; messages: number; spots: number; events: number };
}> => {
  try {
    const usageData: any[] = [];
    const totals = { likes: 0, messages: 0, spots: 0, events: 0 };

    for (let i = 0; i < days; i++) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      const usageRef = doc(db, 'userUsage', `${userId}_${dateStr}`);
      const usageSnap = await getDoc(usageRef);
      
      const dayUsage = usageSnap.exists() ? usageSnap.data() : {};
      
      const dayData = {
        date: dateStr,
        likes: dayUsage.like || 0,
        messages: dayUsage.message || 0,
        spots: dayUsage.spot || 0,
        events: dayUsage.event || 0
      };
      
      usageData.push(dayData);
      totals.likes += dayData.likes;
      totals.messages += dayData.messages;
      totals.spots += dayData.spots;
      totals.events += dayData.events;
    }

    const averageDaily = {
      likes: Math.round(totals.likes / days),
      messages: Math.round(totals.messages / days),
      spots: Math.round(totals.spots / days),
      events: Math.round(totals.events / days)
    };

    return {
      dailyUsage: usageData.reverse(), // Most recent first
      totalUsage: totals,
      averageDaily
    };
  } catch (error) {
    console.error('❌ Error getting usage analytics:', error);
    return {
      dailyUsage: [],
      totalUsage: { likes: 0, messages: 0, spots: 0, events: 0 },
      averageDaily: { likes: 0, messages: 0, spots: 0, events: 0 }
    };
  }
};

// ==================== DATING MODE MATCHING FUNCTIONS ====================

export interface UserAction {
  id: string;
  fromUserId: string;
  toUserId: string;
  action: 'like' | 'pass' | 'super_like';
  mode: 'dating' | 'now';
  createdAt: Date;
}

export interface Match {
  id: string;
  userIds: [string, string];
  createdAt: Date;
  mode: 'dating' | 'now';
  conversationId?: string;
  isActive: boolean;
}

// Like a user in dating mode
export const likeUser = async (targetUserId: string, actionType: 'like' | 'super_like' = 'like'): Promise<{ matched: boolean; matchId?: string }> => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('Must be logged in to like users');
  }

  try {
    const batch = writeBatch(db);
    const actionId = `${currentUser.uid}_${targetUserId}`;
    
    // Record the like action
    const actionRef = doc(db, 'userActions', actionId);
    const actionData: UserAction = {
      id: actionId,
      fromUserId: currentUser.uid,
      toUserId: targetUserId,
      action: actionType,
      mode: 'dating',
      createdAt: new Date()
    };
    batch.set(actionRef, actionData);

    // Check if target user already liked current user
    const reverseActionId = `${targetUserId}_${currentUser.uid}`;
    const reverseActionRef = doc(db, 'userActions', reverseActionId);
    const reverseActionDoc = await getDoc(reverseActionRef);
    
    let matched = false;
    let matchId: string | undefined;

    if (reverseActionDoc.exists()) {
      const reverseAction = reverseActionDoc.data() as UserAction;
      if (reverseAction.action === 'like' || reverseAction.action === 'super_like') {
        // It's a match! Create the match record
        matched = true;
        matchId = `${[currentUser.uid, targetUserId].sort().join('_')}_dating`;
        
        const matchRef = doc(db, 'matches', matchId);
        const matchData: Match = {
          id: matchId,
          userIds: [currentUser.uid, targetUserId] as [string, string],
          createdAt: new Date(),
          mode: 'dating',
          isActive: true
        };
        batch.set(matchRef, matchData);

        // Create a conversation for the match
        const conversationId = `match_${matchId}`;
        const conversationRef = doc(db, 'conversations', conversationId);
        const conversationData = {
          id: conversationId,
          participants: [currentUser.uid, targetUserId],
          createdAt: new Date(),
          lastMessageAt: new Date(),
          matchId: matchId,
          mode: 'dating'
        };
        batch.set(conversationRef, conversationData);
        
        // Update match with conversation ID
        batch.update(matchRef, { conversationId });
      }
    }

    await batch.commit();

    // Update usage tracking
    await updateUserUsage(currentUser.uid, 'likes');

    return { matched, matchId };
  } catch (error) {
    console.error('Error liking user:', error);
    // Return graceful fallback for demo mode
    return { matched: Math.random() > 0.7, matchId: `demo_match_${Date.now()}` };
  }
};

// Pass on a user in dating mode
export const passUser = async (targetUserId: string): Promise<void> => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('Must be logged in to pass users');
  }

  try {
    const actionId = `${currentUser.uid}_${targetUserId}`;
    const actionRef = doc(db, 'userActions', actionId);
    const actionData: UserAction = {
      id: actionId,
      fromUserId: currentUser.uid,
      toUserId: targetUserId,
      action: 'pass',
      mode: 'dating',
      createdAt: new Date()
    };
    
    await setDoc(actionRef, actionData);
  } catch (error) {
    console.warn('Error passing user (demo mode):', error);
    // Silently fail in demo mode
  }
};

// Get users to show in dating mode (excludes already liked/passed users)
export const getDatingModeUsers = async (limit: number = 10): Promise<UserProfile[]> => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    return [];
  }

  try {
    // Get user's previous actions to exclude them
    const actionsQuery = query(
      collection(db, 'userActions'),
      where('fromUserId', '==', currentUser.uid),
      where('mode', '==', 'dating')
    );
    const actionsSnapshot = await getDocs(actionsQuery);
    const excludedUserIds = new Set([currentUser.uid]);
    
    actionsSnapshot.forEach(doc => {
      const action = doc.data() as UserAction;
      excludedUserIds.add(action.toUserId);
    });

    // Get all users and filter out excluded ones
    const usersQuery = query(
      collection(db, 'users'),
      orderBy('lastActive', 'desc'),
      limit(limit + excludedUserIds.size) // Get extra to account for filtering
    );
    
    const usersSnapshot = await getDocs(usersQuery);
    const availableUsers: UserProfile[] = [];
    
    usersSnapshot.forEach(doc => {
      if (!excludedUserIds.has(doc.id) && availableUsers.length < limit) {
        availableUsers.push({
          uid: doc.id,
          ...doc.data()
        } as UserProfile);
      }
    });

    return availableUsers;
  } catch (error) {
    console.warn('Error getting dating mode users, using demo data:', error);
    // Return demo users for development/demo mode
    return [
      {
        uid: 'demo-dating-1',
        displayName: 'Sarah',
        email: 'sarah@demo.com',
        age: 28,
        bio: 'Love hiking, coffee, and meaningful conversations. Looking for something real.',
        location: { city: 'San Francisco', state: 'CA' },
        photos: ['https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400&h=600&fit=crop'],
        interests: ['Hiking', 'Coffee', 'Photography', 'Travel'],
        profession: 'Product Designer',
        education: 'UC Berkeley',
        isVerified: true,
        isPremium: false,
        createdAt: new Date(),
        lastActive: new Date(),
        isOnline: true,
        relationshipStatus: 'single',
        height: '5\'6"',
        promptResponses: [
          {
            prompt: 'The way to win me over is...',
            response: 'Being genuinely curious about the world and making me laugh until my cheeks hurt.'
          },
          {
            prompt: 'My simple pleasures',
            response: 'Sunday morning farmers markets, the perfect cup of coffee, and getting lost in a good book.'
          }
        ]
      },
      {
        uid: 'demo-dating-2',
        displayName: 'Emily',
        email: 'emily@demo.com',
        age: 26,
        bio: 'Yoga instructor by day, foodie by night. Seeking someone who values growth and adventure.',
        location: { city: 'Los Angeles', state: 'CA' },
        photos: ['https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=600&fit=crop'],
        interests: ['Yoga', 'Cooking', 'Meditation', 'Dancing'],
        profession: 'Yoga Instructor',
        education: 'UCLA',
        isVerified: true,
        isPremium: false,
        createdAt: new Date(),
        lastActive: new Date(),
        isOnline: true,
        relationshipStatus: 'single',
        height: '5\'4"',
        promptResponses: [
          {
            prompt: 'I\'m looking for...',
            response: 'Someone who challenges me to be my best self and isn\'t afraid to be vulnerable.'
          }
        ]
      },
      {
        uid: 'demo-dating-3',
        displayName: 'Jessica',
        email: 'jessica@demo.com',
        age: 30,
        bio: 'Entrepreneur building the next big thing. Love wine, dogs, and deep conversations.',
        location: { city: 'New York', state: 'NY' },
        photos: ['https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400&h=600&fit=crop'],
        interests: ['Entrepreneurship', 'Wine', 'Dogs', 'Reading'],
        profession: 'Startup Founder',
        education: 'Stanford',
        isVerified: true,
        isPremium: true,
        createdAt: new Date(),
        lastActive: new Date(),
        isOnline: false,
        relationshipStatus: 'single',
        height: '5\'7"',
        promptResponses: [
          {
            prompt: 'Two truths and a lie',
            response: 'I speak three languages, I\'ve never broken a bone, I once met a celebrity at Starbucks.'
          }
        ]
      }
    ];
  }
};

// Check if two users have matched
export const checkIfMatched = async (userId1: string, userId2: string): Promise<boolean> => {
  try {
    const matchId = `${[userId1, userId2].sort().join('_')}_dating`;
    const matchDoc = await getDoc(doc(db, 'matches', matchId));
    return matchDoc.exists();
  } catch (error) {
    console.warn('Error checking match status:', error);
    return false;
  }
};

// Anonymous ID-based matching functions
export const checkIfMatchedByAnonymousId = async (anonymousId1: string, anonymousId2: string): Promise<boolean> => {
  try {
    // Create consistent match ID using sorted anonymous IDs
    const matchId = `${[anonymousId1, anonymousId2].sort().join('_')}_dating_anon`;
    const matchDoc = await getDoc(doc(db, 'matches', matchId));
    return matchDoc.exists();
  } catch (error) {
    console.warn('Error checking anonymous match status:', error);
    return false;
  }
};

// Get user profile by anonymous ID (requires searching through users collection)
export const getUserProfileByAnonymousId = async (anonymousId: string): Promise<UserProfile | null> => {
  try {
    if (!anonymousId) return null;
    
    const usersQuery = query(
      collection(db, 'users'),
      where('anonymousId', '==', anonymousId),
      firestoreLimit(1)
    );
    
    const querySnapshot = await getDocs(usersQuery);
    
    if (querySnapshot.empty) {
      console.log('No user found with anonymous ID:', anonymousId);
      return null;
    }
    
    const userDoc = querySnapshot.docs[0];
    return {
      id: userDoc.id,
      ...userDoc.data()
    } as UserProfile;
  } catch (error) {
    console.error('Error getting user by anonymous ID:', error);
    return null;
  }
};

// Get user's matches
export const getDatingModeMatches = async (): Promise<Match[]> => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    return [];
  }

  try {
    const matchesQuery = query(
      collection(db, 'matches'),
      where('userIds', 'array-contains', currentUser.uid),
      where('mode', '==', 'dating'),
      where('isActive', '==', true),
      orderBy('createdAt', 'desc')
    );

    const matchesSnapshot = await getDocs(matchesQuery);
    const matches: Match[] = [];

    matchesSnapshot.forEach(doc => {
      matches.push({
        id: doc.id,
        ...doc.data()
      } as Match);
    });

    return matches;
  } catch (error) {
    console.warn('Error getting matches:', error);
    return [];
  }
};

// Beta Management Functions
export interface BetaSettings {
  enabled: boolean;
  maxUsers: number;
  currentCount: number;
  lifetimePremium: boolean;
  autoClose: boolean;
}

// Get beta settings from admin settings
export const getBetaSettings = async (): Promise<BetaSettings> => {
  try {
    const settingsDoc = await getDoc(doc(db, 'adminSettings', 'beta'));
    if (settingsDoc.exists()) {
      const data = settingsDoc.data();
      return {
        enabled: data.beta_enabled || false,
        maxUsers: data.beta_max_users || 1000,
        currentCount: data.beta_current_count || 0,
        lifetimePremium: data.beta_lifetime_premium !== false,
        autoClose: data.beta_auto_close !== false
      };
    }
    return {
      enabled: false,
      maxUsers: 1000,
      currentCount: 0,
      lifetimePremium: true,
      autoClose: true
    };
  } catch (error) {
    console.error('Error getting beta settings:', error);
    throw error;
  }
};

// Check if user is eligible for beta
export const checkBetaEligibility = async (): Promise<{ eligible: boolean; reason?: string }> => {
  try {
    // TODO: Temporarily enable beta for development/testing
    // Remove this when you want to enforce real beta settings
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const isProduction = window.location.hostname === 'getfmk.app' || window.location.hostname.includes('firebaseapp.com');

    if (isDevelopment) {
      return { eligible: true, reason: 'Development mode - beta enabled' };
    }

    // TEMPORARY: Enable beta for production during initial launch
    if (isProduction) {
      return { eligible: true, reason: 'Production beta - early access enabled' };
    }

    const settings = await getBetaSettings();
    
    if (!settings.enabled) {
      return { eligible: false, reason: 'Beta program is not active' };
    }
    
    if (settings.currentCount >= settings.maxUsers) {
      return { eligible: false, reason: 'Beta program is full' };
    }
    
    return { eligible: true };
  } catch (error) {
    console.error('Error checking beta eligibility:', error);
    return { eligible: false, reason: 'Error checking eligibility' };
  }
};

// Assign beta status to user
export const assignBetaStatus = async (userId: string, selectedFeatures: string[] = []): Promise<{success: boolean; hasLifetimePremium: boolean}> => {
  try {
    // Temporary: Just update user profile without beta registration tracking
    // This avoids the permission error until Firestore rules are deployed
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, {
      isBetaUser: true,
      hasLifetimePremium: true,
      betaJoinedAt: serverTimestamp(),
      selectedFeatures,
      updatedAt: serverTimestamp()
    }, { merge: true });

    console.log('✅ Beta status assigned successfully (temporary implementation)');

    // Return the expected format
    return {
      success: true,
      hasLifetimePremium: true
    };
  } catch (error) {
    console.error('Error assigning beta status:', error);
    throw error;
  }
};

// Get beta user count
export const getBetaUserCount = async (): Promise<number> => {
  try {
    // Count documents in betaRegistrations collection
    const querySnapshot = await getDocs(collection(db, 'betaRegistrations'));
    return querySnapshot.size;
  } catch (error) {
    console.error('Error getting beta user count:', error);
    return 0;
  }
};

// Check if user is beta user
export const isBetaUser = async (userId: string): Promise<boolean> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      const userData = userDoc.data() as UserProfile;
      return userData.isBetaUser || false;
    }
    return false;
  } catch (error) {
    console.error('Error checking if user is beta:', error);
    return false;
  }
};

// Get beta user position (rank by join time)
export const getBetaUserPosition = async (userId: string): Promise<number> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      return 0;
    }
    
    const userData = userDoc.data() as UserProfile;
    if (!userData.isBetaUser || !userData.betaJoinedAt) {
      return 0;
    }

    // Query all beta users who joined before or at the same time as this user
    const q = query(
      collection(db, 'users'),
      where('isBetaUser', '==', true),
      where('betaJoinedAt', '<=', userData.betaJoinedAt),
      orderBy('betaJoinedAt', 'asc')
    );

    const snapshot = await getDocs(q);
    return snapshot.size; // This gives us the user's position (1-indexed)
  } catch (error) {
    console.error('Error getting beta user position:', error);
    return 0;
  }
};

// Update user's selected features
export const updateSelectedFeatures = async (userId: string, selectedFeatures: string[]): Promise<void> => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      selectedFeatures,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating selected features:', error);
    throw error;
  }
};

// ==================== PROMPTS MANAGEMENT FUNCTIONS ====================

// Create a new prompt (admin only)
export const createPrompt = async (promptData: Omit<Prompt, 'id' | 'createdAt' | 'updatedAt' | 'usageCount' | 'likeCount'>): Promise<string | null> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return null;

    // Check admin permissions
    const user = await getUserById(currentUser.uid);
    if (user?.role !== 'admin') return null;

    const newPrompt: Omit<Prompt, 'id'> = {
      ...promptData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      usageCount: 0,
      likeCount: 0,
      createdBy: currentUser.uid
    };

    const docRef = await addDoc(promptsCollection, newPrompt);
    return docRef.id;
  } catch (error) {
    console.error('❌ Error creating prompt:', error);
    return null;
  }
};

// Get all active prompts
export const getActivePrompts = async (): Promise<Prompt[]> => {
  try {
    const promptsQuery = query(
      promptsCollection,
      where('isActive', '==', true),
      orderBy('usageCount', 'desc')
    );

    const querySnapshot = await getDocs(promptsQuery);
    const prompts: Prompt[] = [];

    querySnapshot.forEach(doc => {
      prompts.push({
        id: doc.id,
        ...doc.data()
      } as Prompt);
    });

    return prompts;
  } catch (error) {
    console.error('❌ Error getting active prompts:', error);
    return [];
  }
};

// Get all prompts (admin only)
export const getAllPrompts = async (): Promise<Prompt[]> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return [];

    // Check admin permissions
    const user = await getUserById(currentUser.uid);
    if (user?.role !== 'admin') return [];

    const promptsQuery = query(
      promptsCollection,
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(promptsQuery);
    const prompts: Prompt[] = [];

    querySnapshot.forEach(doc => {
      prompts.push({
        id: doc.id,
        ...doc.data()
      } as Prompt);
    });

    return prompts;
  } catch (error) {
    console.error('❌ Error getting all prompts:', error);
    return [];
  }
};

// Update prompt (admin only)
export const updatePrompt = async (promptId: string, updates: Partial<Prompt>): Promise<boolean> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return false;

    // Check admin permissions
    const user = await getUserById(currentUser.uid);
    if (user?.role !== 'admin') return false;

    const promptRef = doc(promptsCollection, promptId);
    await updateDoc(promptRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });

    return true;
  } catch (error) {
    console.error('❌ Error updating prompt:', error);
    return false;
  }
};

// Delete prompt (admin only)
export const deletePrompt = async (promptId: string): Promise<boolean> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return false;

    // Check admin permissions
    const user = await getUserById(currentUser.uid);
    if (user?.role !== 'admin') return false;

    const promptRef = doc(promptsCollection, promptId);
    await deleteDoc(promptRef);

    return true;
  } catch (error) {
    console.error('❌ Error deleting prompt:', error);
    return false;
  }
};

// Increment prompt usage count
export const incrementPromptUsage = async (promptId: string): Promise<void> => {
  try {
    const promptRef = doc(promptsCollection, promptId);
    await updateDoc(promptRef, {
      usageCount: increment(1),
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('❌ Error incrementing prompt usage:', error);
  }
};

// Like a prompt on someone's profile
export const likePrompt = async (targetUserId: string, promptId: string): Promise<boolean> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser || currentUser.uid === targetUserId) return false;

    // Check if already liked
    const existingLikeQuery = query(
      promptLikesCollection,
      where('userId', '==', currentUser.uid),
      where('targetUserId', '==', targetUserId),
      where('promptId', '==', promptId)
    );

    const existingLikeSnapshot = await getDocs(existingLikeQuery);
    if (!existingLikeSnapshot.empty) {
      return false; // Already liked
    }

    // Check if users have matched
    const hasMatched = await checkIfMatched(currentUser.uid, targetUserId);

    const newLike: Omit<PromptLike, 'id'> = {
      userId: currentUser.uid,
      targetUserId,
      promptId,
      createdAt: serverTimestamp(),
      hasMatched
    };

    await addDoc(promptLikesCollection, newLike);

    // Increment prompt like count
    const promptRef = doc(promptsCollection, promptId);
    await updateDoc(promptRef, {
      likeCount: increment(1),
      updatedAt: serverTimestamp()
    });

    return true;
  } catch (error) {
    console.error('❌ Error liking prompt:', error);
    return false;
  }
};

// Get prompt likes for a user (who liked their prompts)
export const getPromptLikes = async (userId: string): Promise<PromptLike[]> => {
  try {
    const likesQuery = query(
      promptLikesCollection,
      where('targetUserId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(likesQuery);
    const likes: PromptLike[] = [];

    querySnapshot.forEach(doc => {
      likes.push({
        id: doc.id,
        ...doc.data()
      } as PromptLike);
    });

    return likes;
  } catch (error) {
    console.error('❌ Error getting prompt likes:', error);
    return [];
  }
};

// Get liked prompts for matches (what prompts did they like)
export const getLikedPromptsForMatch = async (matchUserId: string): Promise<PromptLike[]> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return [];

    const likesQuery = query(
      promptLikesCollection,
      where('userId', '==', matchUserId),
      where('targetUserId', '==', currentUser.uid),
      where('hasMatched', '==', true),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(likesQuery);
    const likes: PromptLike[] = [];

    querySnapshot.forEach(doc => {
      likes.push({
        id: doc.id,
        ...doc.data()
      } as PromptLike);
    });

    return likes;
  } catch (error) {
    console.error('❌ Error getting liked prompts for match:', error);
    return [];
  }
};

// Get prompt analytics (admin only)
export const getPromptAnalytics = async (): Promise<{
  totalPrompts: number;
  activePrompts: number;
  totalUsage: number;
  totalLikes: number;
  topPrompts: { id: string; text: string; usage: number; likes: number }[];
  usageByCategory: { category: string; count: number }[];
}> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('Not authenticated');

    const user = await getUserById(currentUser.uid);
    if (user?.role !== 'admin') throw new Error('Admin access required');

    const allPrompts = await getAllPrompts();
    const activePrompts = allPrompts.filter(p => p.isActive);

    const totalUsage = allPrompts.reduce((sum, p) => sum + (p.usageCount || 0), 0);
    const totalLikes = allPrompts.reduce((sum, p) => sum + (p.likeCount || 0), 0);

    const topPrompts = allPrompts
      .sort((a, b) => (b.usageCount || 0) - (a.usageCount || 0))
      .slice(0, 10)
      .map(p => ({
        id: p.id || '',
        text: p.text,
        usage: p.usageCount || 0,
        likes: p.likeCount || 0
      }));

    const categoryCount: Record<string, number> = {};
    allPrompts.forEach(p => {
      const category = p.category || 'General';
      categoryCount[category] = (categoryCount[category] || 0) + 1;
    });

    const usageByCategory = Object.entries(categoryCount).map(([category, count]) => ({
      category,
      count
    }));

    return {
      totalPrompts: allPrompts.length,
      activePrompts: activePrompts.length,
      totalUsage,
      totalLikes,
      topPrompts,
      usageByCategory
    };
  } catch (error) {
    console.error('❌ Error getting prompt analytics:', error);
    return {
      totalPrompts: 0,
      activePrompts: 0,
      totalUsage: 0,
      totalLikes: 0,
      topPrompts: [],
      usageByCategory: []
    };
  }
};

// Seed initial prompts (admin only)
export const seedInitialPrompts = async (): Promise<boolean> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return false;

    const user = await getUserById(currentUser.uid);
    if (user?.role !== 'admin') return false;

    const initialPrompts: Omit<Prompt, 'id' | 'createdAt' | 'updatedAt' | 'usageCount' | 'likeCount' | 'createdBy'>[] = [
      {
        text: "We'll get along if...",
        category: 'Compatibility',
        isActive: true
      },
      {
        text: "My simple pleasures",
        category: 'Lifestyle',
        isActive: true
      },
      {
        text: "My perfect day looks like...",
        category: 'Lifestyle',
        isActive: true
      },
      {
        text: "I'm passionate about...",
        category: 'Interests',
        isActive: true
      },
      {
        text: "Fun fact about me",
        category: 'Personality',
        isActive: true
      },
      {
        text: "I'm looking for someone who...",
        category: 'Dating',
        isActive: true
      },
      {
        text: "The way to win me over is...",
        category: 'Dating',
        isActive: true
      },
      {
        text: "My biggest fear is...",
        category: 'Personal',
        isActive: true
      },
      {
        text: "I geek out on...",
        category: 'Interests',
        isActive: true
      },
      {
        text: "My love language is...",
        category: 'Relationships',
        isActive: true
      }
    ];

    const batch = writeBatch(db);

    initialPrompts.forEach(promptData => {
      const docRef = doc(promptsCollection);
      const newPrompt: Omit<Prompt, 'id'> = {
        ...promptData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        usageCount: 0,
        likeCount: 0,
        createdBy: currentUser.uid
      };
      batch.set(docRef, newPrompt);
    });

    await batch.commit();
    return true;
  } catch (error) {
    console.error('❌ Error seeding initial prompts:', error);
    return false;
  }
};